import { useState, useRef, useEffect } from "react";
import { getSessionFromUrl, guardarAnalisis, supabase } from "./services/supabase";
import * as d3 from "d3";
import TrendRadar from "./components/TrendRadar";
import Forecast from "./components/Forecast";
import Scenarios from "./components/Scenarios";
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import ExportarPDF from "./ExportarPDF";
import ReactMarkdown from 'react-markdown';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  extractKeywords,
  generatePlan,
  generateFoda,
  generateHorizontes,
  generateImpacto,
  generateMicmac,
  fetchSignalsFromPlumber,
  fetchRegulaciones,
  interpretWithPlumber,
  fetchFinancial,
  fetchSostenibilidad,
} from "./services/api";
import "./index.css";
import "./App.css";

const NAV = [
  { id: "mis-analisis", icon: "📂", label: "Mis análisis", sub: "Historial guardado" },
  { id: "setup", icon: "⚙️", label: "Mi empresa y mi pregunta", sub: "Punto de partida" },
  { id: "radar", icon: "🌐", label: "Mapa de tendencias", sub: "Visualización interactiva" },
  { id: "scanning", icon: "📡", label: "Señales del mercado", sub: "Lo que está pasando afuera" },
  { id: "regulaciones", icon: "⚖️", label: "Marco legal y regulatorio", sub: "Normativa del sector" },
  { id: "forecast", icon: "📈", label: "Proyecciones de mercado", sub: "Tamaño y crecimiento" },
  { id: "scenarios", icon: "🎯", label: "Escenarios estratégicos", sub: "Futuros posibles" },
  { id: "horizontes", icon: "⏳", label: "Visión temporal", sub: "Ahora, próximos años y futuro" },
  { id: "foda", icon: "🔲", label: "Fortalezas y riesgos", sub: "Análisis estratégico" },
  { id: "micmac", icon: "🔗", label: "Variables que mueven el sistema", sub: "Qué impulsa y qué depende" },
  { id: "impacto", icon: "💰", label: "Oportunidad de mercado", sub: "Estimación financiera por escenario" },
  { id: "sostenibilidad", icon: "♻️", label: "Impacto ambiental", sub: "Sostenibilidad por escenario" },
  { id: "plan", icon: "🧭", label: "Mi plan de acción", sub: "Estrategia y modelos de negocio" },
];

// ── ROII y RMI ────────────────────────────────────────────────────────────
const HORIZON_WEIGHTS = {
  "Corto plazo": 1.0,
  "Mediano plazo": 0.7,
  "Largo plazo": 0.4,
};

const PESTEL_WEIGHTS = {
  "Económico": 1.2,
  "Tecnológico": 1.1,
  "Legal": 0.9,
  "Político": 0.8,
  "Social": 0.7,
  "Ambiental": 0.6,
};

function calcROII(sc, perfil = {}) {
  if (!sc.signals.length) return 0;

  const { facturacion = "", plazo = "", obstaculo = "" } = perfil;

  // Factor base desde señales
  const oppWeight = sc.signals
    .filter(s => s.classification === "Oportunidad")
    .reduce((a, s) => a + (+s.impact_probability || 0), 0);
  const riskWeight = sc.signals
    .filter(s => s.classification === "Riesgo")
    .reduce((a, s) => a + (+s.impact_probability || 0), 0);

  const baseROII = oppWeight / (riskWeight + 1);

  // Modificador por perfil empresa
  const factorTamaño = { micro: 0.7, pequena: 0.85, mediana: 1.0, grande: 1.2 }[facturacion] || 1.0;
  const factorPlazo = plazo.includes("Inmediato") ? 0.8 : plazo.includes("3-6") ? 0.9 : plazo.includes("6-12") ? 1.0 : 1.1;
  const factorObstaculo = obstaculo.includes("financiamiento") ? 0.85 : obstaculo.includes("mercados") ? 0.9 : 1.0;

  const roii = baseROII * factorTamaño * factorPlazo * factorObstaculo;
  return Math.round(roii * 100) / 100;
}

function calcRMI(sc, perfil = {}) {
  if (!sc.signals.length) return 1;

  const { facturacion = "", obstaculo = "" } = perfil;

  const riesgosAltos = sc.signals.filter(
    s => s.classification === "Riesgo" && (+s.impact_probability || 0) >= 0.6
  ).length;
  const riesgosMedios = sc.signals.filter(
    s => s.classification === "Riesgo" &&
      (+s.impact_probability || 0) >= 0.3 &&
      (+s.impact_probability || 0) < 0.6
  ).length;

  const penalizacion = (riesgosAltos * 1.5 + riesgosMedios * 0.5) / sc.signals.length;
  const baseRMI = Math.max(0, Math.min(1, 1 - penalizacion));

  // Modificador por perfil
  const factorTamaño = { micro: 0.85, pequena: 0.9, mediana: 1.0, grande: 1.1 }[facturacion] || 1.0;
  const factorObstaculo = obstaculo.includes("regulatori") ? 0.85 : obstaculo.includes("tecnolog") ? 0.9 : 1.0;

  const rmi = Math.min(1, baseRMI * factorTamaño * factorObstaculo);
  return Math.round(rmi * 100) / 100;
}

function MicmacPlot({ variables }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!variables.length || !ref.current) return;
    const el = ref.current;
    const W = el.clientWidth || 500;
    const H = 320;
    const PAD = { top: 20, right: 20, bottom: 40, left: 50 };

    const COLORS = {
      "Poder": "#35627A",
      "Conflicto": "#B46258",
      "Autónoma": "#8E9A98",
      "Resultado": "#059669",
    };

    const motValues = variables.map(v => +v.motricidad);
    const depValues = variables.map(v => +v.dependencia);
    const minMot = Math.min(...motValues) - 2;
    const maxMot = Math.max(...motValues) + 2;
    const minDep = Math.min(...depValues) - 2;
    const maxDep = Math.max(...depValues) + 2;
    const medMot = (minMot + maxMot) / 2;
    const medDep = (minDep + maxDep) / 2;

    d3.select(el).selectAll("*").remove();
    const svg = d3.select(el).append("svg").attr("width", W).attr("height", H);
    const g = svg.append("g").attr("transform", `translate(${PAD.left},${PAD.top})`);
    const iW = W - PAD.left - PAD.right;
    const iH = H - PAD.top - PAD.bottom;

    const xScale = d3.scaleLinear().domain([minDep, maxDep]).range([0, iW]);
    const yScale = d3.scaleLinear().domain([minMot, maxMot]).range([iH, 0]);

    // Fondos de cuadrantes
    [
      { x1: minDep, x2: medDep, y1: medMot, y2: maxMot, color: "rgba(53,98,122,0.06)", label: "PODER" },
      { x1: medDep, x2: maxDep, y1: medMot, y2: maxMot, color: "rgba(180,98,88,0.06)", label: "CONFLICTO" },
      { x1: minDep, x2: medDep, y1: minMot, y2: medMot, color: "rgba(142,154,152,0.06)", label: "AUTÓNOMA" },
      { x1: medDep, x2: maxDep, y1: minMot, y2: medMot, color: "rgba(5,150,105,0.06)", label: "RESULTADO" },
    ].forEach(q => {
      g.append("rect")
        .attr("x", xScale(q.x1)).attr("y", yScale(q.y2))
        .attr("width", xScale(q.x2) - xScale(q.x1))
        .attr("height", yScale(q.y1) - yScale(q.y2))
        .attr("fill", q.color);
      g.append("text")
        .attr("x", xScale((q.x1 + q.x2) / 2))
        .attr("y", yScale(q.y2) + 14)
        .attr("text-anchor", "middle")
        .attr("fill", "#8E9A98")
        .attr("font-size", 8)
        .attr("font-family", "JetBrains Mono, monospace")
        .attr("letter-spacing", 1)
        .text(q.label);
    });

    // Líneas de mediana
    g.append("line")
      .attr("x1", xScale(medDep)).attr("x2", xScale(medDep))
      .attr("y1", 0).attr("y2", iH)
      .attr("stroke", "#ccc").attr("stroke-width", 1)
      .attr("stroke-dasharray", "4,4");
    g.append("line")
      .attr("x1", 0).attr("x2", iW)
      .attr("y1", yScale(medMot)).attr("y2", yScale(medMot))
      .attr("stroke", "#ccc").attr("stroke-width", 1)
      .attr("stroke-dasharray", "4,4");

    // Ejes
    g.append("g").attr("transform", `translate(0,${iH})`)
      .call(d3.axisBottom(xScale).ticks(5))
      .selectAll("text").attr("fill", "#8E9A98").attr("font-size", 9);
    g.append("g")
      .call(d3.axisLeft(yScale).ticks(5))
      .selectAll("text").attr("fill", "#8E9A98").attr("font-size", 9);

    // Labels de ejes
    g.append("text")
      .attr("x", iW / 2).attr("y", iH + 32)
      .attr("text-anchor", "middle")
      .attr("fill", "#8E9A98").attr("font-size", 10)
      .attr("font-family", "JetBrains Mono, monospace")
      .text("Dependencia →");
    g.append("text")
      .attr("transform", "rotate(-90)")
      .attr("x", -iH / 2).attr("y", -36)
      .attr("text-anchor", "middle")
      .attr("fill", "#8E9A98").attr("font-size", 10)
      .attr("font-family", "JetBrains Mono, monospace")
      .text("← Motricidad");

    // Puntos
    variables.forEach((v, i) => {
      const cx = xScale(+v.dependencia);
      const cy = yScale(+v.motricidad);
      const color = COLORS[v.cuadrante] || "#8E9A98";

      g.append("circle")
        .attr("cx", cx).attr("cy", cy)
        .attr("r", 7)
        .attr("fill", color)
        .attr("fill-opacity", 0.85)
        .attr("stroke", "#fff")
        .attr("stroke-width", 1.5);

      g.append("text")
        .attr("x", cx).attr("y", cy + 4)
        .attr("text-anchor", "middle")
        .attr("fill", "#fff")
        .attr("font-size", 7)
        .attr("font-weight", "700")
        .text(`E${i + 1}`);

      g.append("title")
        .text(`${v.event_id} · ${v.cuadrante}\nM: ${v.motricidad} · D: ${v.dependencia}`);
    });

  }, [variables]);

  return <div ref={ref} style={{ width: "100%" }} />;
}

const PREGUNTAS_SUGERIDAS = {
  "Cosméticos naturales": {
    "Lanzamiento de nuevo producto": [
      "¿Qué tendencias en biotecnología están transformando los ingredientes activos para cosméticos naturales en Latinoamérica?",
      "¿Cuáles son los principales riesgos regulatorios para lanzar un cosmético natural en Colombia?",
      "¿Qué oportunidades ofrece la biodiversidad colombiana para diferenciarse en el mercado internacional de cosméticos?",
    ],
    "Expansión a nuevos mercados": [
      "¿Qué mercados internacionales presentan mayor oportunidad para cosméticos naturales colombianos?",
      "¿Cuáles son las barreras regulatorias para exportar cosméticos naturales desde Colombia a Europa?",
      "¿Qué tendencias de consumo sostenible están impulsando la demanda de cosméticos naturales globalmente?",
    ],
    "Inversión en I+D": [
      "¿Qué bioingredientes colombianos tienen mayor potencial para aplicaciones cosméticas innovadoras?",
      "¿Cuáles son las tecnologías de extracción y formulación más prometedoras para cosméticos naturales?",
      "¿Qué líneas de investigación en biotecnología cosmética están atrayendo más inversión en Latinoamérica?",
    ],
    "Cumplimiento regulatorio": [
      "¿Cuáles son los requisitos regulatorios del INVIMA para comercializar cosméticos naturales con ingredientes amazónicos en Colombia?",
      "¿Qué cambios regulatorios recientes de la Comunidad Andina impactan la formulación de cosméticos naturales colombianos?",
      "¿Cómo están evolucionando las normativas de etiquetado y trazabilidad para cosméticos naturales en mercados de exportación?",
    ],
    "Alianzas estratégicas": [
      "¿Qué tipo de alianzas entre empresas de cosméticos naturales y comunidades indígenas están generando mayor valor en Colombia?",
      "¿Qué universidades y centros de investigación colombianos tienen capacidades relevantes para el desarrollo de ingredientes cosméticos naturales?",
      "¿Qué alianzas entre empresas colombianas de cosméticos y distribuidores internacionales están siendo más exitosas en LATAM?",
    ],
    "Optimización de procesos": [
      "¿Qué tecnologías de extracción y formulación están reduciendo costos en la producción de cosméticos naturales en LATAM?",
      "¿Cómo están optimizando su cadena de suministro de ingredientes naturales las empresas cosméticas más competitivas de Colombia?",
      "¿Qué innovaciones en manufactura sostenible están mejorando la eficiencia de empresas de cosméticos naturales en la región?",
    ],
    "Atracción de inversión": [
      "¿Qué fondos de inversión de impacto están financiando empresas de cosméticos naturales en Colombia y LATAM?",
      "¿Qué métricas y argumentos están usando empresas de cosméticos naturales colombianas para atraer inversión internacional?",
      "¿Qué instrumentos de financiamiento de iNNpulsa e INNpulsa Colombia están disponibles para empresas de cosméticos naturales?",
    ],
  },
  "Agroindustria": {
    "Lanzamiento de nuevo producto": [
      "¿Qué subproductos agroindustriales colombianos tienen mayor potencial de valorización en bioeconomía?",
      "¿Qué oportunidades existen para desarrollar bioproductos desde residuos de caña de azúcar o café en Colombia?",
      "¿Cuáles son las tendencias en biorefinería que más impactan a la agroindustria colombiana?",
    ],
    "Expansión a nuevos mercados": [
      "¿Qué mercados internacionales demandan más bioproductos agroindustriales colombianos?",
      "¿Cómo puede la agroindustria colombiana posicionarse en el mercado global de economía circular?",
      "¿Qué certificaciones internacionales facilitan la exportación de bioproductos agroindustriales desde Colombia?",
    ],
    "Inversión en I+D": [
      "¿Qué tecnologías de biorefinería están transformando la agroindustria en Latinoamérica?",
      "¿Cuáles son las oportunidades de innovación en bioplásticos y biomateriales desde residuos agroindustriales?",
      "¿Qué alianzas universidad-empresa están impulsando la innovación en agroindustria sostenible en Colombia?",
    ],
    "Cumplimiento regulatorio": [
      "¿Cuáles son los principales cambios regulatorios del ICA que afectan a las empresas agroindustriales colombianas en 2025?",
      "¿Qué requisitos fitosanitarios están exigiendo los principales mercados de exportación para productos agroindustriales colombianos?",
      "¿Cómo impacta la regulación de biocombustibles y bioproductos al sector agroindustrial colombiano?",
    ],
    "Alianzas estratégicas": [
      "¿Qué alianzas entre empresas agroindustriales y proveedores de bioinsumos están generando mayor competitividad en Colombia?",
      "¿Qué modelos de integración vertical están adoptando las empresas agroindustriales colombianas más exitosas?",
      "¿Qué oportunidades de alianza existen entre el sector agroindustrial colombiano y empresas de biotecnología internacional?",
    ],
    "Optimización de procesos": [
      "¿Qué tecnologías de biorefínería están reduciendo costos y aumentando valor agregado en la agroindustria colombiana?",
      "¿Cómo están optimizando el uso de residuos agroindustriales las empresas más innovadoras de Colombia?",
      "¿Qué innovaciones en logística y cadena de frío están mejorando la competitividad exportadora de la agroindustria colombiana?",
    ],
    "Atracción de inversión": [
      "¿Qué sectores de la agroindustria colombiana están atrayendo mayor inversión extranjera directa en 2025?",
      "¿Qué fondos de inversión en agricultura sostenible están activos en Colombia y LATAM?",
      "¿Qué programas de financiamiento del Ministerio de Agricultura están disponibles para empresas agroindustriales innovadoras?",
    ],
  },
  "Alimentos funcionales": {
    "Lanzamiento de nuevo producto": [
      "¿Qué tendencias en nutraceúticos y alimentos funcionales están transformando el mercado latinoamericano?",
      "¿Qué ingredientes bioactivos colombianos tienen mayor potencial para alimentos funcionales?",
      "¿Cuáles son los requisitos regulatorios para lanzar un alimento funcional en Colombia y exportarlo?",
    ],
    "Expansión a nuevos mercados": [
      "¿Qué mercados internacionales presentan mayor crecimiento en demanda de alimentos funcionales?",
      "¿Cómo pueden las empresas colombianas de alimentos funcionales diferenciarse en mercados europeos y asiáticos?",
      "¿Qué tendencias de salud y bienestar están impulsando el consumo de alimentos funcionales globalmente?",
    ],
    "Inversión en I+D": [
      "¿Qué tecnologías de encapsulación y biodisponibilidad están liderando la innovación en alimentos funcionales?",
      "¿Cuáles son los bioactivos de frutas tropicales colombianas con mayor evidencia científica para alimentos funcionales?",
      "¿Qué líneas de investigación en probióticos y prebióticos tienen más tracción en Latinoamérica?",
    ],
    "Cumplimiento regulatorio": [
      "¿Cuáles son los requisitos del INVIMA para registrar y comercializar alimentos funcionales y nutraceúticos en Colombia?",
      "¿Qué cambios en la regulación de etiquetado nutricional impactan a los fabricantes de alimentos funcionales en Colombia?",
      "¿Cómo están evolucionando las normativas de claims de salud para alimentos funcionales en mercados de exportación clave?",
    ],
    "Alianzas estratégicas": [
      "¿Qué alianzas entre empresas de alimentos funcionales y centros de investigación están generando mayor innovación en Colombia?",
      "¿Qué modelos de co-desarrollo con agricultores locales están usando empresas de alimentos funcionales exitosas en LATAM?",
      "¿Qué alianzas con cadenas de retail y e-commerce están impulsando el crecimiento de alimentos funcionales en Colombia?",
    ],
    "Optimización de procesos": [
      "¿Qué tecnologías de encapsulación y conservación están mejorando la vida útil de alimentos funcionales en Colombia?",
      "¿Cómo están optimizando sus procesos de extracción de bioactivos las empresas de alimentos funcionales más competitivas?",
      "¿Qué innovaciones en manufactura flexible están permitiendo a empresas de alimentos funcionales diversificar su portafolio?",
    ],
    "Atracción de inversión": [
      "¿Qué fondos de impacto y venture capital están invirtiendo en empresas de alimentos funcionales en LATAM?",
      "¿Qué argumentos financieros están usando empresas de alimentos funcionales colombianas para atraer inversión?",
      "¿Qué programas de Innpulsa y el SENA están disponibles para empresas de alimentos funcionales en etapa de crecimiento?",
    ],
  },
  "Bioinsumos agrícolas": {
    "Lanzamiento de nuevo producto": [
      "¿Qué oportunidades existen para bioinsumos de control biológico en la agricultura colombiana?",
      "¿Cuáles son los principales cultivos colombianos con mayor demanda de biofertilizantes y biocontroladores?",
      "¿Qué requisitos exige el ICA para el registro de bioinsumos en Colombia?",
    ],
    "Expansión a nuevos mercados": [
      "¿Qué mercados latinoamericanos presentan mayor demanda de bioinsumos agrícolas sostenibles?",
      "¿Cómo pueden los bioinsumos colombianos competir en mercados internacionales de agricultura sostenible?",
      "¿Qué tendencias regulatorias globales están favoreciendo la adopción de bioinsumos sobre agroquímicos?",
    ],
    "Inversión en I+D": [
      "¿Qué microorganismos colombianos tienen mayor potencial para desarrollo de biofertilizantes innovadores?",
      "¿Cuáles son las tecnologías de formulación más prometedoras para bioinsumos en condiciones tropicales?",
      "¿Qué investigaciones en microbioma del suelo están transformando el mercado de bioinsumos globalmente?",
    ],
    "Cumplimiento regulatorio": [
      "¿Cuáles son los requisitos actuales del ICA para el registro y comercialización de bioinsumos en Colombia?",
      "¿Qué cambios regulatorios están facilitando o dificultando el mercado de bioinsumos en Colombia en 2025?",
      "¿Cómo están evolucionando las normativas de bioseguridad para microorganismos usados en bioinsumos agrícolas?",
    ],
    "Alianzas estratégicas": [
      "¿Qué alianzas entre empresas de bioinsumos y gremios agrícolas están generando mayor adopción en Colombia?",
      "¿Qué modelos de transferencia tecnológica universidad-empresa están impulsando la innovación en bioinsumos colombianos?",
      "¿Qué oportunidades de alianza existen entre fabricantes de bioinsumos colombianos y distribuidores agrícolas internacionales?",
    ],
    "Optimización de procesos": [
      "¿Qué innovaciones en fermentación y formulación están reduciendo costos de producción de bioinsumos en Colombia?",
      "¿Cómo están mejorando la estabilidad y vida útil de sus productos las empresas líderes de bioinsumos en LATAM?",
      "¿Qué tecnologías de control de calidad están adoptando las empresas de bioinsumos más competitivas de la región?",
    ],
    "Atracción de inversión": [
      "¿Qué fondos de inversión en agricultura sostenible están financiando empresas de bioinsumos en Colombia?",
      "¿Qué métricas de impacto ambiental están usando empresas de bioinsumos para atraer inversión de impacto?",
      "¿Qué programas de financiamiento público están disponibles para empresas de bioinsumos en etapa de escala?",
    ],
  },
  "Biotecnología industrial": {
    "Lanzamiento de nuevo producto": [
      "¿Qué oportunidades existen para enzimas industriales y biocatalizadores en la industria colombiana?",
      "¿Cuáles son las aplicaciones de biotecnología industrial con mayor potencial de mercado en Colombia?",
      "¿Qué tendencias en química verde están abriendo nuevos mercados para la biotecnología industrial?",
    ],
    "Expansión a nuevos mercados": [
      "¿Qué sectores industriales colombianos tienen mayor potencial para adoptar soluciones biotecnológicas?",
      "¿Cómo puede la biotecnología industrial colombiana posicionarse en mercados de economía circular?",
      "¿Qué alianzas estratégicas están impulsando la biotecnología industrial en Latinoamérica?",
    ],
    "Inversión en I+D": [
      "¿Qué plataformas biotecnológicas industriales están atrayendo más inversión globalmente en 2025?",
      "¿Cuáles son las oportunidades en biomanufactura y fermentación de precisión para empresas colombianas?",
      "¿Qué desarrollos en biología sintética tienen mayor impacto potencial para la industria colombiana?",
    ],
    "Cumplimiento regulatorio": [
      "¿Cuáles son los marcos regulatorios más relevantes para empresas de biotecnología industrial en Colombia?",
      "¿Cómo está evolucionando la regulación de bioseguridad para organismos modificados en Colombia y LATAM?",
      "¿Qué requisitos regulatorios deben cumplir las empresas colombianas de biotecnología para exportar sus productos?",
    ],
    "Alianzas estratégicas": [
      "¿Qué alianzas entre empresas de biotecnología industrial y el sector manufacturero están generando mayor valor en Colombia?",
      "¿Qué oportunidades de colaboración existen entre biotecnológicas colombianas y empresas farmacéuticas internacionales?",
      "¿Qué modelos de spin-off universitario en biotecnología industrial están siendo más exitosos en LATAM?",
    ],
    "Optimización de procesos": [
      "¿Qué innovaciones en biomanufactura están reduciendo costos y tiempos de producción en biotecnología industrial?",
      "¿Cómo están optimizando sus procesos de escalado las empresas de biotecnología industrial más competitivas de LATAM?",
      "¿Qué tecnologías de automatización y control de procesos están adoptando las biofábricas más avanzadas de la región?",
    ],
    "Atracción de inversión": [
      "¿Qué fondos de venture capital y corporate venture están activos en biotecnología industrial en LATAM?",
      "¿Qué instrumentos de financiamiento de Minciencias están disponibles para empresas de biotecnología industrial?",
      "¿Qué argumentos de retorno y escalabilidad están usando biotecnológicas colombianas para atraer inversión privada?",
    ],
  },
  "Farmacéutica verde": {
    "Lanzamiento de nuevo producto": [
      "¿Qué oportunidades existen para fitofármacos y medicamentos de origen natural en el mercado colombiano?",
      "¿Cuáles son los requisitos del INVIMA para registro de medicamentos fitoterapéuticos en Colombia?",
      "¿Qué plantas medicinales colombianas tienen mayor evidencia científica para desarrollo farmacéutico?",
    ],
    "Expansión a nuevos mercados": [
      "¿Qué mercados internacionales presentan mayor demanda de farmacéuticos de origen natural?",
      "¿Cómo puede la biodiversidad colombiana posicionarse en el mercado global de farmacéutica verde?",
      "¿Qué tendencias regulatorias internacionales están favoreciendo la farmacéutica verde?",
    ],
    "Inversión en I+D": [
      "¿Qué tecnologías de extracción y purificación de principios activos naturales lideran la innovación farmacéutica?",
      "¿Cuáles son las moléculas bioactivas colombianas con mayor potencial farmacéutico sin explotar?",
      "¿Qué líneas de investigación en bioprospección farmacéutica están recibiendo más financiamiento globalmente?",
    ],
    "Cumplimiento regulatorio": [
      "¿Cuáles son los requisitos del INVIMA para el registro de medicamentos fitoterapéuticos y productos naturales en Colombia?",
      "¿Cómo está evolucionando la regulación de plantas medicinales y extractos naturales con uso terapéutico en Colombia?",
      "¿Qué marcos regulatorios internacionales deben cumplir los productos de farmacéutica verde colombiana para exportar?",
    ],
    "Alianzas estratégicas": [
      "¿Qué alianzas entre empresas de farmacéutica verde y comunidades étnicas están generando mayor acceso a recursos naturales?",
      "¿Qué modelos de colaboración entre laboratorios farmacéuticos y centros de bioprospección están siendo más exitosos?",
      "¿Qué oportunidades de alianza existen entre la farmacéutica verde colombiana y el sistema de salud nacional?",
    ],
    "Optimización de procesos": [
      "¿Qué tecnologías de extracción verde están reduciendo costos y mejorando pureza en farmacéutica natural colombiana?",
      "¿Cómo están optimizando sus procesos de estandarización de extractos las empresas de farmacéutica verde más avanzadas?",
      "¿Qué innovaciones en control de calidad están mejorando la competitividad de productos de farmacéutica verde colombiana?",
    ],
    "Atracción de inversión": [
      "¿Qué fondos de inversión en salud natural y bienestar están activos en Colombia y LATAM?",
      "¿Qué argumentos de propiedad intelectual y biodiversidad están usando empresas de farmacéutica verde para atraer capital?",
      "¿Qué instrumentos de financiamiento están disponibles para empresas colombianas en farmacéutica verde y fitoterapéuticos?",
    ],
  },
  "Biocomercio": {
    "Lanzamiento de nuevo producto": [
      "¿Qué productos de biocomercio colombianos tienen mayor potencial en mercados internacionales sostenibles?",
      "¿Cuáles son los criterios de certificación de biocomercio más valorados por compradores internacionales?",
      "¿Qué oportunidades existen para productos no maderables del bosque colombiano en mercados de nicho?",
    ],
    "Expansión a nuevos mercados": [
      "¿Qué mercados europeos y asiáticos tienen mayor demanda de productos de biocomercio colombiano?",
      "¿Cómo pueden las empresas colombianas de biocomercio acceder a mercados premium internacionales?",
      "¿Qué tendencias de consumo responsable están impulsando la demanda de productos de biocomercio?",
    ],
    "Inversión en I+D": [
      "¿Qué innovaciones en procesamiento y valor agregado están transformando el biocomercio colombiano?",
      "¿Cuáles son las oportunidades de investigación en productos amazónicos con potencial de biocomercio?",
      "¿Qué alianzas entre comunidades y empresas están generando más valor en el biocomercio colombiano?",
    ],
    "Cumplimiento regulatorio": [
      "¿Cuáles son los requisitos del Protocolo de Nagoya que deben cumplir las empresas colombianas de biocomercio?",
      "¿Qué certificaciones de comercio justo y sostenibilidad son más valoradas en los mercados destino del biocomercio colombiano?",
      "¿Cómo está evolucionando la regulación de acceso y distribución de beneficios para recursos genéticos en Colombia?",
    ],
    "Alianzas estratégicas": [
      "¿Qué modelos de alianza entre empresas de biocomercio y comunidades campesinas están generando mayor equidad y valor?",
      "¿Qué oportunidades de alianza existen entre empresas colombianas de biocomercio y compradores internacionales de ingredientes naturales?",
      "¿Qué redes y plataformas de biocomercio están facilitando conexiones entre productores colombianos y mercados globales?",
    ],
    "Optimización de procesos": [
      "¿Qué innovaciones en procesamiento y valor agregado están mejorando la rentabilidad del biocomercio colombiano?",
      "¿Cómo están optimizando su logística y cadena de custodia las empresas de biocomercio más competitivas de Colombia?",
      "¿Qué tecnologías de trazabilidad están usando las empresas líderes de biocomercio para garantizar sostenibilidad verificable?",
    ],
    "Atracción de inversión": [
      "¿Qué fondos de inversión de impacto en biodiversidad están financiando empresas de biocomercio en Colombia?",
      "¿Qué métricas de impacto social y ambiental están usando empresas de biocomercio colombianas para atraer inversores?",
      "¿Qué programas de financiamiento de WWF, Conservation International u otras ONGs están disponibles para biocomercio colombiano?",
    ],
  },
};

const PRODUCTIVIDAD_SECTORIAL = {
  "Cosméticos naturales": { capital: 0.72, materia_prima: 0.68, energia: 0.65, talento: 0.70 },
  "Alimentos funcionales": { capital: 0.68, materia_prima: 0.71, energia: 0.63, talento: 0.67 },
  "Agroindustria": { capital: 0.65, materia_prima: 0.74, energia: 0.61, talento: 0.64 },
  "Bioinsumos agrícolas": { capital: 0.70, materia_prima: 0.66, energia: 0.64, talento: 0.69 },
  "Biotecnología industrial": { capital: 0.75, materia_prima: 0.63, energia: 0.67, talento: 0.73 },
  "Farmacéutica verde": { capital: 0.73, materia_prima: 0.65, energia: 0.66, talento: 0.72 },
  "Biocomercio": { capital: 0.67, materia_prima: 0.69, energia: 0.62, talento: 0.68 },
};

function calcProductividad(sector, escenario) {
  const base = PRODUCTIVIDAD_SECTORIAL[sector] ||
    { capital: 0.65, materia_prima: 0.65, energia: 0.60, talento: 0.65 };
  const prod_base = (base.capital + base.materia_prima + base.energia + base.talento) / 4;
  const delta = (escenario.roii - 1) * 0.15 - (1 - escenario.rmi) * 0.08;
  return Math.min(Math.max(prod_base + delta, 0), 1);
}

export default function App() {
  const [page, setPage] = useState("setup");
  const [session, setSession] = useState(null);
  const [modoRestaurado, setModoRestaurado] = useState(false);
  const [analisisSeleccionado, setAnalisisSeleccionado] = useState(null);
  const [logicaInnovacion, setLogicaInnovacion] = useState("");
  const [misAnalisis, setMisAnalisis] = useState([]);
  const [problem, setProblem] = useState("");
  const [sector, setSector] = useState("");
  const [objetivo, setObjetivo] = useState("");
  const [etapa, setEtapa] = useState("");
  const [facturacion, setFacturacion] = useState("");
  const [mercados, setMercados] = useState([]);
  const [obstaculoPrincipal, setObstaculoPrincipal] = useState("");
  const [plazoDecision, setPlazoDecision] = useState("");
  const [incrementoFacturacion, setIncrementoFacturacion] = useState("");
  const [limit, setLimit] = useState(50);
  const [signals, setSignals] = useState([]);
  const [regulaciones, setRegulaciones] = useState(null);
  const [keywords, setKeywords] = useState("");
  const [forecast, setForecast] = useState(null);
  const [financial, setFinancial] = useState([]);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState("agente listo");
  const [error, setError] = useState(null);
  const [log, setLog] = useState([]);
  const [foda, setFoda] = useState(null);
  const [impacto, setImpacto] = useState(null);
  const [horizontes, setHorizontes] = useState(null);
  const [micmac, setMicmac] = useState(null);
  const [montecarlo, setMontecarlo] = useState(null);
  const [departamento, setDepartamento] = useState("");
  const [sostenibilidad, setSostenibilidad] = useState(null);
  const [mcLoading, setMcLoading] = useState(false);

  useEffect(() => {
    getSessionFromUrl().then(s => {
      if (s) {
        setSession(s);
        setLog(l => [...l, `✓ Sesión iniciada — ${s.user.email}`]);
      }
    }).catch(e => {
      console.log("Session error:", e);
    });
  }, []);

  const addLog = (msg, type = "info") => {
    const prefix = type === "error" ? "❌" : type === "warn" ? "⚠️" : "✓";
    setLog(l => [...l, `[${new Date().toLocaleTimeString()}] ${prefix} ${msg}`]);
  };

  function buildEmpresaContext() {
    const parts = [];
    if (logicaInnovacion) parts.push(`Lógica de innovación: ${logicaInnovacion === "push" ? "Market Push (tecnología buscando mercado)" : "Market Pull (demanda buscando solución)"}`);
    if (departamento) parts.push(`Departamento de operación: ${departamento}`);
    if (sector) parts.push(`Sector: ${sector}`);
    if (objetivo) parts.push(`Objetivo estratégico: ${objetivo}`);
    if (etapa) parts.push(`Etapa de la empresa: ${etapa}`);
    if (mercados.length > 0) parts.push(`Mercados donde opera: ${mercados.join(", ")}`);
    if (obstaculoPrincipal) parts.push(`Principal obstáculo: ${obstaculoPrincipal}`);
    if (plazoDecision) parts.push(`Plazo de decisión: ${plazoDecision}`);
    if (incrementoFacturacion && incrementoFacturacion !== "0")
      parts.push(`Incremento de facturación esperado: ${incrementoFacturacion}%`);
    if (facturacion) {
      const facMap = {
        "micro": "Microempresa (< COP 500M)",
        "pequena": "Pequeña empresa (COP 500M - 5.000M)",
        "mediana": "Mediana empresa (COP 5.000M - 30.000M)",
        "grande": "Gran empresa (> COP 30.000M)",
      };
      parts.push(`Facturación: ${facMap[facturacion]}`);
    }
    return parts.length > 0 ? parts.join(" | ") : "";
  }

  // ── Main analysis ────────────────────────────────────────────────────────
  async function runAnalysis() {
    if (!problem.trim()) return;
    setModoRestaurado(false);
    setLoading(true);
    setError(null);
    setSignals([]); setForecast(null); setPlan(null); setFinancial([]); setFoda(null); setHorizontes(null); setRegulaciones(null); setMicmac(null); setMontecarlo(null);
    addLog("Iniciando análisis...");
    setStatusMsg("extrayendo keywords...");

    let kws;
    try {
      const kw_raw = await extractKeywords(problem);
      kws = kw_raw || problem;
      setKeywords(kws);
      addLog(`Keywords: ${kws}`);
    } catch (e) {
      kws = problem;
      addLog("No se pudieron extraer keywords, usando problema original", "warn");
    }

    setStatusMsg("consultando data lake...");
    let sigs = [];
    try {
      sigs = await fetchSignalsFromPlumber(kws, 180, limit, sector);
      if (sigs.length === 0) {
        addLog("No se encontraron señales para este problema.", "warn");
        setError("No se encontraron señales. Intenta reformular el problema con otros términos.");
        setStatusMsg("agente listo");
        setLoading(false);
        return;
      }
      setSignals(sigs);
      addLog(`${sigs.length} señales recuperadas`);
    } catch (e) {
      addLog("Error al consultar el data lake.", "error");
      setError("No se pudo conectar con el servidor. Verifica tu conexión e intenta de nuevo.");
      setStatusMsg("error");
      setLoading(false);
      return;
    }

    setStatusMsg("cargando contexto financiero e interpretación...");
    const contexto = buildEmpresaContext();
    const problemaCompleto = contexto
      ? `${problem}\n\nContexto empresarial: ${contexto}`
      : problem;

    const [fc, fin] = await Promise.all([
      interpretWithPlumber(problemaCompleto, sigs, 'general').catch(() => null),
      fetchFinancial().catch(() => []),
    ]);

    setForecast(fc || "No se pudo generar la interpretación automática.");
    setFinancial(fin);
    addLog(`Interpretación generada · ${fin.length} indicadores financieros`);

    setStatusMsg("agente listo");
    setLoading(false);
    setPage("radar");
  }
  async function cargarMisAnalisis() {
    if (!session) return;
    console.log("Cargando análisis para user:", session.user.id);
    const { data, error } = await supabase
      .from('projects')
      .select('id, empresa, sector, problema, created_at, signals_count')
      .eq('user_id', session.user.id)
      .order('created_at', { ascending: false })
      .limit(10);
    console.log("Resultado:", { data, error });
    if (data) setMisAnalisis(data);
  }

  async function restaurarAnalisis(a) {
    const { data } = await supabase
      .from('projects')
      .select('*')
      .eq('id', a.id)
      .single();

    if (!data) return;

    setProblem(data.problema || "");
    setSector(data.sector || "");
    setLogicaInnovacion(data.logica_innovacion || "");
    setMercados(data.mercados ? data.mercados.split(",").filter(m => m) : []);
    setObjetivo(data.objetivo || "");
    setEtapa(data.etapa || "");
    setFacturacion(data.facturacion || "");
    setMercados(data.mercados ? data.mercados.split(",").filter(m => m) : []);
    setObjetivo(data.objetivo || "");
    setObstaculoPrincipal(data.obstaculo || "");
    setPlazoDecision(data.plazo || "");
    setDepartamento(data.departamento || "");
    setSostenibilidad(data.sostenibilidad ? (typeof data.sostenibilidad === 'string' ? JSON.parse(data.sostenibilidad) : data.sostenibilidad) : null);
    setFoda(data.foda ? (typeof data.foda === 'string' ? JSON.parse(data.foda) : data.foda) : null);
    setMicmac(data.micmac ? (typeof data.micmac === 'string' ? JSON.parse(data.micmac) : data.micmac) : null);
    setHorizontes(data.horizontes ? (typeof data.horizontes === 'string' ? JSON.parse(data.horizontes) : data.horizontes) : null);
    setImpacto(data.impacto ? (typeof data.impacto === 'string' ? JSON.parse(data.impacto) : data.impacto) : null);
    setPlan(data.plan ? { executive: data.plan } : null);
    setAnalisisSeleccionado(null);
    setModoRestaurado(true);
    setSignals(data.senales ? (typeof data.senales === 'string' ? JSON.parse(data.senales) : data.senales) : []);
    setPage("setup");
    addLog(`Análisis restaurado — ${data.problema?.substring(0, 50)}...`);
  }

  async function eliminarAnalisis(id) {
    if (!window.confirm("¿Eliminar este análisis permanentemente?")) return;
    const { error } = await supabase.from('projects').delete().eq('id', id);
    if (!error) {
      addLog("Análisis eliminado");
      cargarMisAnalisis();
      if (analisisSeleccionado?.id === id) setAnalisisSeleccionado(null);
    } else {
      addLog("Error al eliminar análisis", "error");
    }
  }

  async function runPlan() {
    console.log("runPlan called - signals:", signals.length, "problem:", problem?.substring(0, 50));
    if (!signals.length) return;
    setLoading(true);
    setError(null);
    setStatusMsg("generando plan...");
    addLog("Generando plan estratégico...");
    try {
      const scens = buildScenarios(signals);
      const scensSummary = scens.slice(0, 4).map(s =>
        `- ${s.cluster} (${s.scenario_type}) | ROII: ${s.roii} | RMI: ${s.rmi} | Señales: ${s.signals.length}`
      ).join('\n');

      // Contexto de sostenibilidad por escenario
      const sostenibilidadCtx = sostenibilidad?.escenarios_impacto?.length > 0
        ? sostenibilidad.escenarios_impacto.map(e =>
          `- ${e.cluster}: Huella hídrica ${e.huella_hidrica}/10 | CO2 ${e.co2_index}/10 | Balance: ${e.balance}`
        ).join('\n')
        : "";

      const indicadoresCtx = sostenibilidad?.indicadores
        ? `Departamento: ${sostenibilidad.departamento} | Biodiversidad: ${sostenibilidad.indicadores.biodiversidad} | Cobertura boscosa: ${sostenibilidad.indicadores.cobertura_boscosa}% | Índice hídrico: ${sostenibilidad.indicadores.indice_hidrico}/10`
        : "";

      const p = await generatePlan(problem, signals.length, scens.length, scensSummary, {
        sector,
        facturacion,
        plazo: plazoDecision,
        obstaculo: obstaculoPrincipal,
        sostenibilidad_escenarios: sostenibilidadCtx,
        indicadores_ambientales: indicadoresCtx,
        incremento_facturacion: incrementoFacturacion,
        objetivo: objetivo,
        mercados: mercados.join(", "),
        departamento: departamento,
      }, signals);

      console.log("Plan recibido en App:", p);
      if (p && (p.executive || p.recommendations)) {
        setPlan({
          executive: Array.isArray(p.executive) ? p.executive[0] : p.executive,
          recommendations: Array.isArray(p.recommendations) ? p.recommendations[0] : p.recommendations,
          canvas: p.canvas || null,
        });
        addLog("Plan generado");
      } else {
        addLog("El modelo no generó el plan. Intenta de nuevo.", "warn");
        setError("No se pudo generar el plan. Intenta de nuevo en unos segundos.");
      }
    } catch (e) {
      addLog("Error al generar el plan estratégico", "error");
      setError("Error al conectar con el servidor para generar el plan.");
    }
    setStatusMsg("agente listo");
    setLoading(false);
  }

  async function exportarPDF(elementId, nombreArchivo) {
    const elemento = document.getElementById(elementId);
    if (!elemento) return;
    setStatusMsg("exportando PDF...");
    try {
      const canvas = await html2canvas(elemento, { scale: 2, useCORS: true });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`${nombreArchivo}.pdf`);
    } catch (e) {
      addLog("Error al exportar PDF", "error");
    }
    setStatusMsg("agente listo");
  }
  async function runFoda() {
    if (!signals.length) return;
    setLoading(true);
    setStatusMsg("generando FODA...");
    addLog("Generando análisis FODA...");
    try {
      const f = await generateFoda(problem, signals);
      if (f) {
        setFoda(f);
        addLog("FODA generado");
      } else {
        addLog("No se pudo generar el FODA", "warn");
      }
    } catch (e) {
      addLog("Error al generar FODA", "error");
    }
    setStatusMsg("agente listo");
    setLoading(false);
  }

  function runHorizontes() {
    if (!signals.length) return;
    const h1 = signals.filter(s => s.time_horizon === "Corto plazo");
    const h2 = signals.filter(s => s.time_horizon === "Mediano plazo");
    const h3 = signals.filter(s => s.time_horizon === "Largo plazo");
    const analisis = generateHorizontes(problem, h1, h2, h3);
    setHorizontes({ h1, h2, h3, analisis });
    addLog("Horizontes generados");
  }

  async function runImpacto() {
    if (!signals.length) return;
    setLoading(true);
    setStatusMsg("generando impacto financiero...");
    addLog("Generando análisis de impacto financiero...");
    try {
      const contexto = buildEmpresaContext();
      const problemaCompleto = contexto
        ? `${problem}\n\nContexto empresarial: ${contexto}`
        : problem;

      const scens = buildScenarios(signals);
      const escenariosPayload = scens.slice(0, 4).map(sc => ({
        cluster: sc.cluster,
        scenario_type: sc.scenario_type,
        roii: sc.roii,
        rmi: sc.rmi,
        signals: sc.signals.slice(0, 8).map(s => ({
          Title: s.Title,
          fuente_tipo: s.fuente_tipo,
          classification: s.classification,
          pestel_category: s.pestel_category,
          time_horizon: s.time_horizon,
          impact_probability: s.impact_probability,
        }))
      }));

      const result = await generateImpacto(problemaCompleto, escenariosPayload, sector, facturacion);
      if (result && result.escenarios) {
        setImpacto(result);
        addLog("Impacto financiero generado");
        runMontecarloConEscenarios(result.escenarios);

        // Análisis de sostenibilidad
        console.log("Departamento al llamar sostenibilidad:", departamento);
        if (departamento) {
          const sostenibilidadData = await fetchSostenibilidad(
            departamento,
            sector,
            problem,
            result.escenarios,
            incrementoFacturacion,
          );
          if (sostenibilidadData) {
            console.log("Sostenibilidad COMPLETA:", JSON.stringify(sostenibilidadData.indicadores));
            setSostenibilidad(sostenibilidadData);
            addLog("Análisis de sostenibilidad generado");
          }
        }
      } else {
        addLog("No se pudo generar el impacto financiero", "warn");
      }
    } catch (e) {
      addLog("Error al generar impacto financiero", "error");
    }
    setStatusMsg("agente listo");
    setLoading(false);
  }

  function runMontecarloConEscenarios(escenarios) {
    console.log("MC escenarios recibidos:", escenarios.map(e => ({
      cluster: e.cluster,
      opp_usd: e.oportunidad_usd,
      narrativa: (Array.isArray(e.narrativa) ? e.narrativa[0] : e.narrativa)?.substring(0, 40)
    })));
    setMcLoading(true);
    addLog("Simulación Monte Carlo iniciada automáticamente...");
    const worker = new Worker(
      new URL('./workers/montecarlo.worker.js', import.meta.url),
      { type: 'module' }
    );
    const escenariosNorm = escenarios.map(esc => {
      const clusterStr = Array.isArray(esc.cluster) ? esc.cluster[0] : esc.cluster;
      const narrativa = Array.isArray(esc.narrativa) ? esc.narrativa[0] : esc.narrativa;
      const opp_usd = Array.isArray(esc.oportunidad_usd)
        ? parseFloat(esc.oportunidad_usd[0])
        : parseFloat(esc.oportunidad_usd || 0);
      const roii_raw = narrativa?.match(/ROII[^\d]*([\d.]+)/)?.[1];
      const roii = roii_raw ? parseFloat(roii_raw) : 1.0;
      return { ...esc, cluster: clusterStr, roii, oportunidad_usd: opp_usd };
    });
    worker.postMessage({ escenarios: escenariosNorm, n: 100000 });
    worker.onmessage = (e) => {
      setMontecarlo(e.data.resultados);
      setMcLoading(false);
      addLog("Monte Carlo completado");
      worker.terminate();
    };
    worker.onerror = () => {
      setMcLoading(false);
      worker.terminate();
    };
  }

  async function runMicmac() {
    if (!signals.length) return;
    setLoading(true);
    setStatusMsg("generando MICMAC...");
    addLog("Generando matriz de impacto cruzado...");
    try {
      const result = await generateMicmac(signals, 15);
      console.log("Result en App:", result);
      if (result && result.variables && result.variables.length > 0) {
        setMicmac(result);
        addLog("MICMAC generado");
      } else {
        addLog("No se pudo generar el MICMAC", "warn");
      }
    } catch (e) {
      addLog("Error al generar MICMAC", "error");
    }
    setStatusMsg("agente listo");
    setLoading(false);
  }

  async function runRegulaciones() {
    if (regulaciones) return; // ya cargadas
    setLoading(true);
    setStatusMsg("cargando regulaciones...");
    addLog("Cargando marco normativo de bioeconomía...");
    try {
      const r = await fetchRegulaciones(sector);
      setRegulaciones(r);
      addLog(`${r.total} regulaciones cargadas`);
    } catch (e) {
      addLog("Error al cargar regulaciones", "error");
    }
    setStatusMsg("agente listo");
    setLoading(false);
  }

  // ── Build scenarios con ROII y RMI ──────────────────────────────────────
  function buildScenarios(sigs) {
    const byCluster = {};
    sigs.forEach(s => {
      const c = s.cluster || "Sin clasificar";
      if (!byCluster[c]) byCluster[c] = { cluster: c, signals: [], risks: 0, opps: 0 };
      byCluster[c].signals.push(s);
      if (s.classification === "Riesgo") byCluster[c].risks++;
      else byCluster[c].opps++;
    });
    return Object.values(byCluster).map(sc => {
      const validUmapX = sc.signals.filter(s => s.umap_x && s.umap_x !== 'NA' && !isNaN(parseFloat(s.umap_x)));
      const validUmapY = sc.signals.filter(s => s.umap_y && s.umap_y !== 'NA' && !isNaN(parseFloat(s.umap_y)));
      const base = {
        ...sc,
        avg_impact: sc.signals.reduce((a, b) => a + (+b.impact_probability || 0), 0) / sc.signals.length,
        scenario_type: sc.risks > sc.opps ? "Crisis potencial" : "Oportunidad estratégica",
        n_risk: sc.risks,
        n_opp: sc.opps,
        umap_cx: validUmapX.length > 0
          ? validUmapX.reduce((a, b) => a + parseFloat(b.umap_x), 0) / validUmapX.length
          : 5,
        umap_cy: validUmapY.length > 0
          ? validUmapY.reduce((a, b) => a + parseFloat(b.umap_y), 0) / validUmapY.length
          : 5,
      };
      return {
        ...base, roii: calcROII(base, { facturacion, plazo: plazoDecision, obstaculo: obstaculoPrincipal }),
        rmi: calcRMI(base, { facturacion, obstaculo: obstaculoPrincipal })
      };
    });
  }

  const scenarios = buildScenarios(signals);

  // ── Scenario summary stats ───────────────────────────────────────────────
  const bestROII = scenarios.length ? scenarios.reduce((a, b) => a.roii > b.roii ? a : b) : null;
  const worstRMI = scenarios.length ? scenarios.reduce((a, b) => a.rmi < b.rmi ? a : b) : null;
  const avgROII = scenarios.length ? Math.round(scenarios.reduce((a, b) => a + b.roii, 0) / scenarios.length * 100) / 100 : 0;
  const avgRMI = scenarios.length ? Math.round(scenarios.reduce((a, b) => a + b.rmi, 0) / scenarios.length * 100) / 100 : 0;

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="sf-shell">
      {/* Sidebar */}
      {/* ── NUEVO SIDEBAR con grupos colapsables estilo ITONICS ── */}
      <aside style={{
        width: 260, minWidth: 260, height: "100vh",
        background: "#1a2744", display: "flex", flexDirection: "column",
        position: "sticky", top: 0, overflowY: "auto",
        borderRight: "1px solid rgba(255,255,255,0.06)",
      }}>
        {/* Logo */}
        <div style={{
          padding: "20px 18px 14px", borderBottom: "1px solid rgba(255,255,255,0.08)"
        }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: "#fff", letterSpacing: "-0.5px" }}>
            Bio<span style={{ color: "#059669" }}>scope</span>
          </div>
          <div style={{ fontSize: 10, color: "#64748b", marginTop: 2, letterSpacing: "0.5px" }}>
            BIOECONOMIC INTELLIGENCE
          </div>
        </div>

        {/* Mis análisis */}
        <div style={{ padding: "8px 10px 4px" }}>
          <div
            onClick={() => setPage("mis-analisis")}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "8px 10px", borderRadius: 8, cursor: "pointer",
              background: page === "mis-analisis" ? "rgba(5,150,105,0.15)" : "transparent",
              border: page === "mis-analisis" ? "1px solid rgba(5,150,105,0.3)" : "1px solid transparent",
              transition: "all 0.15s",
            }}
          >
            <span style={{ fontSize: 16 }}>📂</span>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#e2e8f0" }}>Mis análisis</div>
              <div style={{ fontSize: 10, color: "#64748b" }}>Historial guardado</div>
            </div>
          </div>
        </div>

        {/* Setup */}
        <div style={{ padding: "4px 10px" }}>
          <div
            onClick={() => setPage("setup")}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "8px 10px", borderRadius: 8, cursor: "pointer",
              background: page === "setup" ? "rgba(5,150,105,0.15)" : "transparent",
              border: page === "setup" ? "1px solid rgba(5,150,105,0.3)" : "1px solid transparent",
              transition: "all 0.15s",
            }}
          >
            <span style={{ fontSize: 16 }}>⚙️</span>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#e2e8f0" }}>Mi empresa y mi pregunta</div>
              <div style={{ fontSize: 10, color: "#64748b" }}>Punto de partida</div>
            </div>
            {signals.length > 0 && (
              <span style={{
                marginLeft: "auto", fontSize: 9, fontWeight: 700,
                background: "#059669", color: "#fff",
                padding: "1px 6px", borderRadius: 20,
              }}>✓</span>
            )}
          </div>
        </div>

        {/* Separador */}
        <div style={{ height: 1, background: "rgba(255,255,255,0.06)", margin: "8px 0" }} />

        {/* GRUPO: Inteligencia Competitiva */}
        {(() => {
          const isActive = ["radar", "scanning", "regulaciones", "forecast", "scenarios", "horizontes", "foda", "micmac", "impacto"].includes(page);
          const [open, setOpen] = useState(true);
          const items = [
            { id: "radar", icon: "🌐", label: "Mapa de tendencias" },
            { id: "scanning", icon: "📡", label: "Señales del mercado" },
            { id: "regulaciones", icon: "⚖️", label: "Marco legal" },
            { id: "forecast", icon: "📈", label: "Proyecciones" },
            { id: "scenarios", icon: "🎯", label: "Escenarios" },
            { id: "horizontes", icon: "⏳", label: "Visión temporal" },
            { id: "foda", icon: "🔲", label: "Fortalezas y riesgos" },
            { id: "micmac", icon: "🔗", label: "Variables del sistema" },
            { id: "impacto", icon: "💰", label: "Oportunidad de mercado" },
          ];
          const completados = items.filter(i => {
            if (i.id === "radar" || i.id === "scanning") return signals.length > 0;
            if (i.id === "regulaciones") return true;
            if (i.id === "forecast") return signals.length > 0;
            if (i.id === "scenarios") return scenarios.length > 0;
            if (i.id === "horizontes") return horizontes != null;
            if (i.id === "foda") return foda != null;
            if (i.id === "micmac") return micmac != null;
            if (i.id === "impacto") return impacto != null;
            return false;
          }).length;

          return (
            <div style={{ padding: "0 10px" }}>
              {/* Header del grupo */}
              <div
                onClick={() => setOpen(!open)}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "6px 8px", borderRadius: 8, cursor: "pointer",
                  background: isActive ? "rgba(53,98,122,0.2)" : "transparent",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 14 }}>🔍</span>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.5px" }}>
                      INTELIGENCIA COMPETITIVA
                    </div>
                    <div style={{ fontSize: 10, color: "#475569" }}>
                      {completados}/{items.length} módulos
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  {/* Progress bar */}
                  <div style={{ width: 32, height: 3, background: "rgba(255,255,255,0.1)", borderRadius: 2 }}>
                    <div style={{
                      width: `${(completados / items.length) * 100}%`, height: "100%",
                      background: "#059669", borderRadius: 2, transition: "width 0.3s"
                    }} />
                  </div>
                  <span style={{ fontSize: 10, color: "#64748b" }}>{open ? "▲" : "▼"}</span>
                </div>
              </div>

              {/* Items del grupo */}
              {open && (
                <div style={{ marginTop: 2, marginBottom: 4 }}>
                  {items.map(item => {
                    const isItemActive = page === item.id;
                    const done = completados > 0 && items.findIndex(i => i.id === item.id) < completados;
                    const disabled = signals.length === 0 && !modoRestaurado;
                    return (
                      <div
                        key={item.id}
                        onClick={() => !disabled && setPage(item.id)}
                        style={{
                          display: "flex", alignItems: "center", gap: 8,
                          padding: "6px 8px 6px 20px", borderRadius: 6,
                          cursor: disabled ? "not-allowed" : "pointer",
                          opacity: disabled ? 0.35 : 1,
                          background: isItemActive ? "rgba(5,150,105,0.12)" : "transparent",
                          borderLeft: isItemActive ? "2px solid #059669" : "2px solid transparent",
                          marginLeft: 4, transition: "all 0.12s",
                        }}
                      >
                        <span style={{ fontSize: 13 }}>{item.icon}</span>
                        <span style={{
                          fontSize: 11, fontWeight: isItemActive ? 600 : 400,
                          color: isItemActive ? "#e2e8f0" : "#94a3b8",
                        }}>{item.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}

        <div style={{ height: 1, background: "rgba(255,255,255,0.06)", margin: "8px 0" }} />

        {/* GRUPO: Inteligencia Sostenible */}
        {(() => {
          const isActive = page === "sostenibilidad";
          const done = sostenibilidad != null;
          return (
            <div style={{ padding: "0 10px" }}>
              <div
                onClick={() => (signals.length > 0 || modoRestaurado) && setPage("sostenibilidad")}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "8px 10px", borderRadius: 8,
                  cursor: (signals.length === 0 && !modoRestaurado) ? "not-allowed" : "pointer",
                  opacity: (signals.length === 0 && !modoRestaurado) ? 0.35 : 1,
                  background: isActive ? "rgba(5,150,105,0.15)" : "transparent",
                  border: isActive ? "1px solid rgba(5,150,105,0.3)" : "1px solid transparent",
                }}
              >
                <span style={{ fontSize: 16 }}>♻️</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.5px" }}>
                    INTELIGENCIA SOSTENIBLE
                  </div>
                  <div style={{ fontSize: 10, color: "#475569" }}>Impacto ambiental</div>
                </div>
                {done && <span style={{ fontSize: 9, background: "#059669", color: "#fff", padding: "1px 6px", borderRadius: 20, fontWeight: 700 }}>✓</span>}
              </div>

              {/* Mini KPIs de sostenibilidad si hay datos */}
              {sostenibilidad && (
                <div style={{
                  display: "grid", gridTemplateColumns: "1fr 1fr",
                  gap: 4, margin: "4px 4px 8px",
                }}>
                  {[
                    { label: "Temp.", value: sostenibilidad.indicadores?.temperatura ? `${sostenibilidad.indicadores.temperatura}°C` : "N/D" },
                    { label: "Hidrico", value: `${sostenibilidad.indicadores?.indice_hidrico}/10` },
                    { label: "Bosque", value: `${sostenibilidad.indicadores?.cobertura_boscosa}%` },
                    { label: "Bio", value: sostenibilidad.indicadores?.biodiversidad || "—" },
                  ].map((kpi, i) => (
                    <div key={i} style={{
                      background: "rgba(255,255,255,0.04)", borderRadius: 4,
                      padding: "3px 6px",
                    }}>
                      <div style={{ fontSize: 8, color: "#475569" }}>{kpi.label}</div>
                      <div style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>{kpi.value}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        <div style={{ height: 1, background: "rgba(255,255,255,0.06)", margin: "8px 0" }} />

        {/* GRUPO: Estrategia Integrada */}
        {(() => {
          const isActive = page === "plan";
          const done = plan != null;
          return (
            <div style={{ padding: "0 10px" }}>
              <div
                onClick={() => (signals.length > 0 || modoRestaurado) && setPage("plan")}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "8px 10px", borderRadius: 8,
                  cursor: (signals.length === 0 && !modoRestaurado) ? "not-allowed" : "pointer",
                  opacity: (signals.length === 0 && !modoRestaurado) ? 0.35 : 1,
                  background: isActive ? "rgba(5,150,105,0.15)" : "transparent",
                  border: isActive ? "1px solid rgba(5,150,105,0.3)" : "1px solid transparent",
                }}
              >
                <span style={{ fontSize: 16 }}>🧭</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.5px" }}>
                    ESTRATEGIA INTEGRADA
                  </div>
                  <div style={{ fontSize: 10, color: "#475569" }}>Plan + modelos de negocio</div>
                </div>
                {done && <span style={{ fontSize: 9, background: "#059669", color: "#fff", padding: "1px 6px", borderRadius: 20, fontWeight: 700 }}>✓</span>}
              </div>

              {/* Resumen del plan si existe */}
              {plan && (
                <div style={{
                  margin: "4px 4px 8px", padding: "6px 8px",
                  background: "rgba(5,150,105,0.08)", borderRadius: 6,
                  borderLeft: "2px solid #059669",
                }}>
                  <div style={{ fontSize: 9, color: "#059669", fontWeight: 700, marginBottom: 2 }}>
                    PLAN GENERADO
                  </div>
                  <div style={{ fontSize: 9, color: "#64748b", lineHeight: 1.4 }}>
                    {plan.canvas?.canvas?.length > 0
                      ? `${plan.canvas.canvas.length} modelos de negocio`
                      : "Plan estratégico listo"}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Status bar */}
        <div style={{
          marginTop: "auto", padding: "12px 16px",
          borderTop: "1px solid rgba(255,255,255,0.06)",
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <div style={{
            width: 7, height: 7, borderRadius: "50%",
            background: loading ? "#D97706" : error ? "#B46258" : "#059669",
            boxShadow: loading ? "0 0 0 2px rgba(217,119,6,0.3)" : error ? "0 0 0 2px rgba(180,98,88,0.3)" : "0 0 0 2px rgba(5,150,105,0.3)",
          }} />
          <div style={{ fontSize: 10, color: "#64748b" }}>{statusMsg}</div>
        </div>
      </aside>

      {/* Main */}
      <main className="sf-main">
        <div className="sf-topbar">
          <div className="sf-breadcrumb">BioScope / <b>{NAV.find(n => n.id === page)?.label}</b></div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {session && signals.length > 0 && (
              <button
                onClick={async () => {
                  const result = await guardarAnalisis(session, {
                    empresa: session.user.user_metadata?.full_name || "",
                    sector,
                    problema: problem,
                    departamento,
                    signals_count: signals.length,
                    foda,
                    micmac: micmac || null,
                    sostenibilidad: sostenibilidad || null,
                    horizontes,
                    impacto,
                    plan: plan ? `${plan.executive || ''}\n\n${plan.recommendations || ''}` : null,
                    logica_innovacion: logicaInnovacion,
                    mercados: mercados.join(","),
                    objetivo,
                    etapa,
                    facturacion,
                    obstaculo: obstaculoPrincipal,
                    plazo: plazoDecision,
                    senales: signals.map(s => ({
                      Title: s.Title,
                      Source: s.Source,
                      pestel_category: s.pestel_category,
                      classification: s.classification,
                      impact_probability: s.impact_probability,
                      time_horizon: s.time_horizon,
                      cluster: s.cluster,
                      url: s.url,
                      umap_x: parseFloat(Array.isArray(s.umap_x) ? s.umap_x[0] : s.umap_x) || 0,
                      umap_y: parseFloat(Array.isArray(s.umap_y) ? s.umap_y[0] : s.umap_y) || 0,
                    })),
                  });
                  if (result) addLog("✅ Análisis guardado en tu cuenta");
                  else addLog("❌ Error al guardar el análisis", "error");
                }}
                style={{
                  padding: "6px 14px", borderRadius: 8, cursor: "pointer",
                  fontSize: 11, fontWeight: 600,
                  border: "1px solid rgba(53,98,122,0.3)",
                  background: "rgba(53,98,122,0.08)", color: "#35627A",
                }}
              >
                💾 Guardar análisis
              </button>
            )}
            {session && (
              <div style={{ fontSize: 11, color: "#8E9A98" }}>
                {session.user.email}
              </div>
            )}
            <div className="sf-badge">{NAV.find(n => n.id === page)?.id}</div>
          </div>
        </div>

        <div className="sf-content">
          {error && (
            <div style={{
              background: "rgba(180,98,88,0.08)",
              border: "1px solid rgba(180,98,88,0.25)",
              borderRadius: 8, padding: "12px 16px", marginBottom: 16,
              color: "#8a3f35", fontSize: 13, fontFamily: "var(--font-mono)",
              display: "flex", alignItems: "center", gap: 10,
            }}>
              <span>⚠️</span><span>{error}</span>
              <button onClick={() => setError(null)}
                style={{ marginLeft: "auto", background: "none", border: "none", color: "#8a3f35", cursor: "pointer", fontSize: 16 }}>×</button>
            </div>
          )}

          {/* SETUP */}
          {page === "setup" && (
            <div>
              <div className="sf-msg">
                <div className="sf-avatar">🧠</div>
                <div className="sf-bubble">
                  <p>Bienvenido a <span className="sf-accent">BioScope</span>. Soy tu agente de inteligencia bioeconómica.</p>
                  <p>Cuéntame sobre tu empresa y objetivo estratégico para personalizar el análisis.</p>
                </div>
              </div>

              {/* 1. Punto de partida */}
              <div className="sf-card">
                <div className="sf-card-title"><span>🧭</span> Punto de partida</div>
                <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                  {[
                    { value: "push", icon: "🔬", label: "Tengo una tecnología", sub: "Busco mercados donde aplicarla" },
                    { value: "pull", icon: "🛒", label: "Identifiqué una demanda", sub: "Busco cómo satisfacerla" },
                  ].map(({ value, icon, label, sub }) => (
                    <button key={value}
                      onClick={() => setLogicaInnovacion(value)}
                      style={{
                        flex: 1, padding: "12px 14px", borderRadius: 8,
                        border: `1px solid ${logicaInnovacion === value ? "#35627A" : "#e2e5e4"}`,
                        background: logicaInnovacion === value ? "rgba(53,98,122,0.08)" : "#fff",
                        cursor: "pointer", textAlign: "left",
                      }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: logicaInnovacion === value ? "#35627A" : "#1a1a1a" }}>
                        {icon} {label}
                      </div>
                      <div style={{ fontSize: 10, color: "#8E9A98", marginTop: 4 }}>{sub}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="sf-card">
                <div className="sf-card-title"><span>🏭</span> Perfil de tu empresa</div>
                <div style={{ marginBottom: 16 }}>
                  <label className="sf-label">Sector principal</label>
                  <select className="sf-select" value={sector} onChange={e => setSector(e.target.value)}
                    style={{
                      width: "100%", marginTop: 6, padding: "10px 12px", borderRadius: 8,
                      border: "1px solid #e2e5e4", fontSize: 13, background: "#fff"
                    }}>
                    <option value="">Selecciona un sector...</option>
                    <option value="Agroindustria">Agroindustria</option>
                    <option value="Cosméticos naturales">Cosméticos naturales</option>
                    <option value="Bioinsumos agrícolas">Bioinsumos agrícolas</option>
                    <option value="Farmacéutica verde">Farmacéutica verde</option>
                    <option value="Alimentos funcionales">Alimentos funcionales</option>
                    <option value="Biotecnología industrial">Biotecnología industrial</option>
                    <option value="Biocomercio">Biocomercio</option>
                    <option value="Otro">Otro</option>
                  </select>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label className="sf-label">Objetivo estratégico</label>
                  <select className="sf-select" value={objetivo} onChange={e => setObjetivo(e.target.value)}
                    style={{
                      width: "100%", marginTop: 6, padding: "10px 12px", borderRadius: 8,
                      border: "1px solid #e2e5e4", fontSize: 13, background: "#fff"
                    }}>
                    <option value="">Selecciona un objetivo...</option>
                    <option value="Lanzamiento de nuevo producto">Lanzamiento de nuevo producto</option>
                    <option value="Expansión a nuevos mercados">Expansión a nuevos mercados</option>
                    <option value="Inversión en I+D">Inversión en I+D</option>
                    <option value="Cumplimiento regulatorio">Cumplimiento regulatorio</option>
                    <option value="Alianzas estratégicas">Alianzas estratégicas</option>
                    <option value="Optimización de procesos">Optimización de procesos</option>
                    <option value="Atracción de inversión">Atracción de inversión</option>
                  </select>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label className="sf-label">Etapa de la empresa</label>
                  <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                    {[
                      { value: "Startup", icon: "🌱" },
                      { value: "Crecimiento", icon: "📈" },
                      { value: "Consolidada", icon: "🏢" },
                      { value: "Corporación", icon: "🌐" },
                    ].map(({ value, icon }) => (
                      <button key={value}
                        onClick={() => setEtapa(value)}
                        style={{
                          flex: "1 1 120px", padding: "10px 14px", borderRadius: 8,
                          border: `1px solid ${etapa === value ? "#35627A" : "#e2e5e4"}`,
                          background: etapa === value ? "rgba(53,98,122,0.08)" : "#fff",
                          color: etapa === value ? "#35627A" : "#555",
                          fontSize: 12, fontWeight: etapa === value ? 600 : 400,
                          cursor: "pointer", textAlign: "center",
                        }}>
                        {icon} {value}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label className="sf-label">Facturación anual aproximada</label>
                  <select className="sf-select" value={facturacion} onChange={e => setFacturacion(e.target.value)}
                    style={{
                      width: "100%", marginTop: 6, padding: "10px 12px", borderRadius: 8,
                      border: "1px solid #e2e5e4", fontSize: 13, background: "#fff"
                    }}>
                    <option value="">Selecciona un rango...</option>
                    <option value="micro">Menos de COP 500M (~USD 125K)</option>
                    <option value="pequena">COP 500M – 5.000M (~USD 1.25M)</option>
                    <option value="mediana">COP 5.000M – 30.000M (~USD 7.5M)</option>
                    <option value="grande">Más de COP 30.000M (~USD 7.5M+)</option>
                  </select>
                </div>
                {/* Departamento */}
                <div style={{ marginBottom: 16 }}>
                  <label className="sf-label">Departamento donde opera</label>
                  <select value={departamento} onChange={e => setDepartamento(e.target.value)}
                    style={{
                      width: "100%", marginTop: 6, padding: "10px 12px", borderRadius: 8,
                      border: "1px solid #e2e5e4", fontSize: 13, background: "#fff"
                    }}>
                    <option value="">Selecciona un departamento...</option>
                    <option value="Amazonas">Amazonas</option>
                    <option value="Antioquia">Antioquia</option>
                    <option value="Arauca">Arauca</option>
                    <option value="Atlántico">Atlántico</option>
                    <option value="Bogotá D.C.">Bogotá D.C.</option>
                    <option value="Bolívar">Bolívar</option>
                    <option value="Boyacá">Boyacá</option>
                    <option value="Caldas">Caldas</option>
                    <option value="Caquetá">Caquetá</option>
                    <option value="Casanare">Casanare</option>
                    <option value="Cauca">Cauca</option>
                    <option value="Cesar">Cesar</option>
                    <option value="Chocó">Chocó</option>
                    <option value="Córdoba">Córdoba</option>
                    <option value="Cundinamarca">Cundinamarca</option>
                    <option value="Guainía">Guainía</option>
                    <option value="Guaviare">Guaviare</option>
                    <option value="Huila">Huila</option>
                    <option value="La Guajira">La Guajira</option>
                    <option value="Magdalena">Magdalena</option>
                    <option value="Meta">Meta</option>
                    <option value="Nariño">Nariño</option>
                    <option value="Norte de Santander">Norte de Santander</option>
                    <option value="Putumayo">Putumayo</option>
                    <option value="Quindío">Quindío</option>
                    <option value="Risaralda">Risaralda</option>
                    <option value="San Andrés y Providencia">San Andrés y Providencia</option>
                    <option value="Santander">Santander</option>
                    <option value="Sucre">Sucre</option>
                    <option value="Tolima">Tolima</option>
                    <option value="Valle del Cauca">Valle del Cauca</option>
                    <option value="Vaupés">Vaupés</option>
                    <option value="Vichada">Vichada</option>
                  </select>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label className="sf-label">Mercados donde opera</label>
                  <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                    {[
                      { value: "Local", label: "🏙️ Local" },
                      { value: "Nacional", label: "🇨🇴 Nacional" },
                      { value: "Exportación LATAM", label: "🌎 LATAM" },
                      { value: "Exportación global", label: "🌐 Global" },
                    ].map(({ value, label }) => (
                      <button key={value}
                        onClick={() => setMercados(prev =>
                          prev.includes(value) ? prev.filter(m => m !== value) : [...prev, value]
                        )}
                        style={{
                          flex: "1 1 100px", padding: "10px 14px", borderRadius: 8,
                          border: `1px solid ${mercados.includes(value) ? "#35627A" : "#e2e5e4"}`,
                          background: mercados.includes(value) ? "rgba(53,98,122,0.08)" : "#fff",
                          color: mercados.includes(value) ? "#35627A" : "#555",
                          fontSize: 12, fontWeight: mercados.includes(value) ? 600 : 400,
                          cursor: "pointer", textAlign: "center",
                        }}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label className="sf-label">Principal obstáculo actual</label>
                  <select value={obstaculoPrincipal} onChange={e => setObstaculoPrincipal(e.target.value)}
                    style={{
                      width: "100%", marginTop: 6, padding: "10px 12px", borderRadius: 8,
                      border: "1px solid #e2e5e4", fontSize: 13, background: "#fff"
                    }}>
                    <option value="">Selecciona un obstáculo...</option>
                    <option value="Acceso a financiamiento">Acceso a financiamiento</option>
                    <option value="Barreras regulatorias">Barreras regulatorias</option>
                    <option value="Falta de tecnología">Falta de tecnología</option>
                    <option value="Competencia intensa">Competencia intensa</option>
                    <option value="Acceso a mercados">Acceso a mercados</option>
                    <option value="Talento humano">Talento humano</option>
                    <option value="Costos de producción">Costos de producción</option>
                  </select>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label className="sf-label">Plazo para tomar la decisión estratégica</label>
                  <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                    {[
                      { value: "Inmediato (< 3 meses)", label: "⚡ Inmediato" },
                      { value: "Corto plazo (3-6 meses)", label: "📅 3-6 meses" },
                      { value: "Mediano plazo (6-12 meses)", label: "🗓️ 6-12 meses" },
                      { value: "Largo plazo (> 1 año)", label: "🔭 Largo plazo" },
                    ].map(({ value, label }) => (
                      <button key={value}
                        onClick={() => setPlazoDecision(value)}
                        style={{
                          flex: "1 1 100px", padding: "10px 14px", borderRadius: 8,
                          border: `1px solid ${plazoDecision === value ? "#35627A" : "#e2e5e4"}`,
                          background: plazoDecision === value ? "rgba(53,98,122,0.08)" : "#fff",
                          color: plazoDecision === value ? "#35627A" : "#555",
                          fontSize: 12, fontWeight: plazoDecision === value ? 600 : 400,
                          cursor: "pointer", textAlign: "center",
                        }}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label className="sf-label">📈 Incremento de facturación esperado con este negocio</label>
                  <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                    {[
                      { value: "25", label: "Hasta 25%", desc: "Conservador" },
                      { value: "50", label: "25% - 50%", desc: "Moderado" },
                      { value: "100", label: "50% - 100%", desc: "Ambicioso" },
                      { value: "200", label: "+100%", desc: "Escala significativa" },
                      { value: "0", label: "Sin proyección", desc: "No definido" },
                    ].map(({ value, label, desc }) => (
                      <button key={value}
                        onClick={() => setIncrementoFacturacion(value)}
                        style={{
                          flex: "1 1 100px", padding: "10px 14px", borderRadius: 8,
                          border: `1px solid ${incrementoFacturacion === value ? "#059669" : "#e2e5e4"}`,
                          background: incrementoFacturacion === value ? "rgba(5,150,105,0.08)" : "#fff",
                          color: incrementoFacturacion === value ? "#059669" : "#555",
                          fontSize: 12, fontWeight: incrementoFacturacion === value ? 600 : 400,
                          cursor: "pointer", textAlign: "center",
                        }}>
                        <div>{label}</div>
                        <div style={{ fontSize: 10, color: "#8E9A98", marginTop: 2 }}>{desc}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* 3. Preguntas sugeridas */}
              {sector && objetivo && (
                <div style={{ marginTop: 4, marginBottom: 4 }}>
                  <div style={{
                    fontSize: 10, color: "#8E9A98", fontFamily: "JetBrains Mono, monospace",
                    textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, paddingLeft: 4,
                  }}>
                    💡 Preguntas sugeridas para {sector} · {objetivo}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {(PREGUNTAS_SUGERIDAS[sector]?.[objetivo] || []).map((pregunta, i) => (
                      <button key={i}
                        onClick={() => setProblem(pregunta)}
                        style={{
                          textAlign: "left", padding: "10px 14px",
                          borderRadius: 8, cursor: "pointer",
                          fontSize: 12, lineHeight: 1.5,
                          border: "1px solid rgba(53,98,122,0.2)",
                          background: problem === pregunta ? "rgba(53,98,122,0.08)" : "#fafafa",
                          color: problem === pregunta ? "#35627A" : "#555",
                          fontWeight: problem === pregunta ? 600 : 400,
                          transition: "all 0.15s",
                        }}
                        onMouseEnter={e => e.target.style.background = "rgba(53,98,122,0.06)"}
                        onMouseLeave={e => e.target.style.background = problem === pregunta ? "rgba(53,98,122,0.08)" : "#fafafa"}
                      >
                        ▶ {pregunta}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* 4. Problema o pregunta estratégica */}
              <div className="sf-card">
                <div className="sf-card-title"><span>🎯</span> Problema o pregunta estratégica</div>
                <textarea className="sf-textarea" rows={3}
                  placeholder="Ej: ¿Qué tendencias en biotecnología están transformando la industria cosmética natural en Latinoamérica?"
                  value={problem} onChange={e => setProblem(e.target.value)} />
                <div style={{ marginBottom: 16, marginTop: 16 }}>
                  <label className="sf-label">Número de señales a recuperar: {limit}</label>
                  <input type="range" min={20} max={200} step={10} value={limit}
                    onChange={e => setLimit(+e.target.value)} style={{ width: "100%", marginTop: 6 }} />
                </div>
                <button className="sf-btn sf-btn-primary" style={{ marginTop: 8, width: "100%" }}
                  onClick={runAnalysis} disabled={loading || !problem.trim()}>
                  {loading ? "⏳ Analizando..." : "🚀 Iniciar análisis"}
                </button>
              </div>

              {log.length > 0 && (
                <div className="sf-card">
                  <div className="sf-card-title"><span>📋</span> Log</div>
                  <pre className="sf-log">{log.join("\n")}</pre>
                </div>
              )}
            </div>
          )}

          {/* TREND RADAR */}
          {page === "radar" && (
            <div>
              <div className="sf-msg">
                <div className="sf-avatar">🎯</div>
                <div className="sf-bubble">
                  <p><span className="sf-accent">Mapa de señales de mercado</span> — Visualización de señales por similitud temática.</p>
                  <p>Cada burbuja representa una señal del entorno de bioeconomía. Los colores indican el tipo de señal (política, económica, social, tecnológica, ambiental o legal) y el tamaño refleja la probabilidad de impacto.</p>
                </div>
              </div>
              <div className="sf-card">
                <div className="sf-card-title"><span>🎯</span> Radar de tendencias — {signals.length} señales</div>
                <TrendRadar signals={signals} width={560} height={520} />
              </div>
              <div className="sf-metrics">
                <div className="sf-metric"><div className="sf-metric-value">{signals.length}</div><div className="sf-metric-label">señales</div></div>
                <div className="sf-metric"><div className="sf-metric-value" style={{ color: "#B46258" }}>{signals.filter(s => s.classification === "Riesgo").length}</div><div className="sf-metric-label">riesgos</div></div>
                <div className="sf-metric"><div className="sf-metric-value" style={{ color: "#35627A" }}>{signals.filter(s => s.classification === "Oportunidad").length}</div><div className="sf-metric-label">oportunidades</div></div>
                <div className="sf-metric"><div className="sf-metric-value">{scenarios.length}</div><div className="sf-metric-label">escenarios</div></div>
              </div>
            </div>
          )}

          {/* SCANNING */}
          {page === "scanning" && (
            <div>
              <div className="sf-msg">
                <div className="sf-avatar">🔭</div>
                <div className="sf-bubble">
                  <p><span className="sf-accent">Scanning.</span> Feed de señales del mercado recuperadas para tu problema.</p>
                </div>
              </div>
              <div className="sf-card">
                <div className="sf-card-title"><span>📡</span> Feed de señales ({signals.length})</div>
                <div className="rss-feed">
                  {signals.slice(0, 30).map((s, i) => (
                    <div key={i} className="rss-card">
                      <div className="rss-header">
                        {s.url && s.url.length > 5 ? (
                          <a href={s.url} target="_blank" rel="noopener noreferrer"
                            style={{ color: "#35627A", textDecoration: "none", fontWeight: 600 }}>
                            {s.Title} ↗
                          </a>
                        ) : (
                          <div className="rss-title">{s.Title}</div>
                        )}
                        <div className="rss-badges">
                          <span className={`rss-badge ${s.classification === "Oportunidad" ? "badge-oportunidad" : "badge-riesgo"}`}>
                            {s.classification === "Oportunidad" ? "↑" : "↓"} {s.classification}
                          </span>
                          <span className={`rss-badge ${+s.impact_probability >= 0.66 ? "badge-impacto-alto" : "badge-impacto-medio"}`}>
                            ⚡ {(+s.impact_probability * 100 || 0).toFixed(0)}%
                          </span>
                          <span className="rss-badge badge-pestel">{s.pestel_category}</span>
                        </div>
                      </div>
                      <div className="rss-meta">
                        <span>📰 {s.Source}</span>
                        <span>📅 {s.Date}</span>
                        <span>🌐 {s.cluster}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* FORECAST */}
          {page === "forecast" && (
            <div>
              <div className="sf-msg">
                <div className="sf-avatar">📈</div>
                <div className="sf-bubble">
                  <p><span className="sf-accent">Forecast.</span> Horizonte temporal de impacto, contexto financiero e interpretación estratégica.</p>
                </div>
              </div>
              <Forecast signals={signals} financial={financial} forecastText={forecast} />
            </div>
          )}

          {/* SCENARIOS */}
          {page === "scenarios" && (
            <div>
              <div className="sf-msg">
                <div className="sf-avatar">🌐</div>
                <div className="sf-bubble">
                  <p><span className="sf-accent">Escenarios.</span> Mapa estratégico de futuros posibles con indicadores ROII y RMI. Clic en cada burbuja para ver el horizonte temporal de impacto.</p>
                </div>
              </div>
              <Scenarios scenarios={scenarios} />
            </div>
          )}

          {/* HORIZONTES */}
          {page === "horizontes" && (
            <div>
              <div className="sf-msg">
                <div className="sf-avatar">⏳</div>
                <div className="sf-bubble">
                  <p><span className="sf-accent">Horizontes</span> — Organización estratégica de señales por ventana temporal de impacto.</p>
                </div>
              </div>
              <div className="sf-card">
                <button className="sf-btn sf-btn-primary" style={{ width: "100%" }}
                  onClick={runHorizontes} disabled={loading}>
                  {loading ? "⏳ Generando..." : "⏳ Generar análisis de horizontes"}
                </button>
              </div>
              {horizontes && horizontes.analisis && (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {[
                    { key: "h1", label: "H1 · Corto plazo (0-3 años)", color: "#B46258", sigs: horizontes.h1 },
                    { key: "h2", label: "H2 · Mediano plazo (3-10 años)", color: "#35627A", sigs: horizontes.h2 },
                    { key: "h3", label: "H3 · Largo plazo (+10 años)", color: "#059669", sigs: horizontes.h3 },
                  ].map(({ key, label, color, sigs }) => {
                    const a = horizontes.analisis[key];
                    if (!a) return null;
                    return (
                      <div key={key} className="sf-card" style={{ borderLeft: `4px solid ${color}` }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                          <div className="sf-card-title" style={{ color, margin: 0 }}>{label}</div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{
                              padding: "3px 8px", borderRadius: 6,
                              fontSize: 9, fontWeight: 700,
                              fontFamily: "JetBrains Mono, monospace",
                              background: `${color}15`, color,
                              border: `1px solid ${color}30`,
                            }}>
                              MRL {key === "h1" ? "6-8" : key === "h2" ? "4-6" : "1-3"}
                            </span>
                            <span style={{
                              padding: "3px 10px", borderRadius: 20,
                              fontSize: 11, fontWeight: 600,
                              background: `${color}15`, color,
                            }}>{sigs.length} señales</span>
                          </div>
                        </div>
                        <div style={{
                          fontSize: 10, color: "#8E9A98",
                          fontFamily: "JetBrains Mono, monospace",
                          marginBottom: 12, padding: "4px 8px",
                          background: `${color}08`, borderRadius: 4,
                        }}>
                          {key === "h1" && "MRL 6-8 · Capacidad de producción disponible o en escala"}
                          {key === "h2" && "MRL 4-6 · Producción en desarrollo, validación de procesos"}
                          {key === "h3" && "MRL 1-3 · Concepto de manufactura en etapa inicial"}
                        </div>
                        <p style={{ fontSize: 13, color: "#1a1a1a", marginBottom: 12, lineHeight: 1.6 }}>
                          {a.descripcion}
                        </p>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                          <div>
                            <div style={{ fontSize: 10, fontFamily: "JetBrains Mono, monospace", color: "#059669", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Oportunidades</div>
                            <ul style={{ margin: 0, paddingLeft: 16 }}>
                              {(a.oportunidades || []).map((o, i) => (
                                <li key={i} style={{ fontSize: 12, color: "#1a1a1a", marginBottom: 4, lineHeight: 1.5 }}>{o}</li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <div style={{ fontSize: 10, fontFamily: "JetBrains Mono, monospace", color: "#B46258", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Riesgos</div>
                            <ul style={{ margin: 0, paddingLeft: 16 }}>
                              {(a.riesgos || []).map((r, i) => (
                                <li key={i} style={{ fontSize: 12, color: "#1a1a1a", marginBottom: 4, lineHeight: 1.5 }}>{r}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                        <div style={{
                          padding: "10px 14px", borderRadius: 6,
                          background: `${color}08`, fontSize: 12,
                          color: "#1a1a1a", lineHeight: 1.6,
                          borderLeft: `2px solid ${color}`,
                        }}>
                          <strong>Acción recomendada:</strong> {a.accion}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* FODA */}
          {page === "foda" && (
            <div>
              <div className="sf-msg">
                <div className="sf-avatar">🔲</div>
                <div className="sf-bubble">
                  <p><span className="sf-accent">Análisis FODA</span> — Fortalezas, Oportunidades, Debilidades y Amenazas identificadas desde las señales del entorno de bioeconomía.</p>
                </div>
              </div>
              <div className="sf-card">
                <button className="sf-btn sf-btn-primary" style={{ width: "100%" }}
                  onClick={runFoda} disabled={loading}>
                  {loading ? "⏳ Generando..." : "🔲 Generar análisis estratégico"}
                </button>
              </div>
              {foda && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  {[
                    { key: "fortalezas", label: "💪 Fortalezas", color: "#35627A", bg: "rgba(53,98,122,0.06)" },
                    { key: "oportunidades", label: "🚀 Oportunidades", color: "#059669", bg: "rgba(5,150,105,0.06)" },
                    { key: "debilidades", label: "⚠️ Debilidades", color: "#B46258", bg: "rgba(180,98,88,0.06)" },
                    { key: "amenazas", label: "🛑 Amenazas", color: "#8a3f35", bg: "rgba(138,63,53,0.06)" },
                  ].map(({ key, label, color, bg }) => (
                    <div key={key} className="sf-card" style={{ background: bg, borderLeft: `3px solid ${color}` }}>
                      <div className="sf-card-title" style={{ color }}><span>{label}</span></div>
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {(foda[key] || []).map((item, i) => (
                          <li key={i} style={{ fontSize: 13, color: "#1a1a1a", marginBottom: 8, lineHeight: 1.5 }}>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* REGULACIONES */}
          {page === "regulaciones" && (
            <div>
              <div className="sf-msg">
                <div className="sf-avatar">⚖️</div>
                <div className="sf-bubble">
                  <p><span className="sf-accent">Marco Normativo</span> — Regulaciones colombianas relevantes para la bioeconomía empresarial.</p>
                </div>
              </div>

              {!regulaciones && (
                <div className="sf-card">
                  <button className="sf-btn sf-btn-primary" style={{ width: "100%" }}
                    onClick={runRegulaciones} disabled={loading}>
                    {loading ? "⏳ Cargando..." : "⚖️ Cargar marco normativo"}
                  </button>
                </div>
              )}

              {regulaciones && (
                <div>
                  <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                    <button
                      onClick={() => exportarPDF("regulaciones-content", "bioscope-regulaciones")}
                      style={{
                        padding: "8px 16px", borderRadius: 8, cursor: "pointer",
                        fontSize: 12, fontWeight: 600, border: "1px solid #e2e5e4",
                        background: "#fff", color: "#35627A", display: "flex", alignItems: "center", gap: 6,
                      }}>
                      📄 Exportar PDF
                    </button>
                  </div>

                  <div id="regulaciones-content">
                    <div className="sf-card">
                      <div className="sf-card-title"><span>📋</span> Normativa clave de bioeconomía colombiana</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {(regulaciones.normativa || []).map((reg, i) => (
                          <div key={i} style={{
                            padding: "12px 14px", borderRadius: 8,
                            border: "1px solid #e2e5e4",
                            background: reg.categoria === "Regulación vigente" ? "rgba(53,98,122,0.04)" :
                              reg.categoria === "Política pública" ? "rgba(5,150,105,0.04)" :
                                "rgba(166,169,208,0.06)",
                          }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                              <div style={{ flex: 1 }}>
                                <a href={reg.url} target="_blank" rel="noopener noreferrer"
                                  style={{ fontSize: 13, fontWeight: 600, color: "#35627A", textDecoration: "none", lineHeight: 1.4, display: "block", marginBottom: 4 }}>
                                  {reg.titulo} ↗
                                </a>
                                <div style={{ fontSize: 11, color: "#8E9A98" }}>
                                  {reg.entidad} · {String(Array.isArray(reg.fecha) ? reg.fecha[0] : reg.fecha || "").substring(0, 10)}
                                </div>
                              </div>
                              <span style={{
                                flexShrink: 0, padding: "3px 8px", borderRadius: 20,
                                fontSize: 9, fontWeight: 600,
                                fontFamily: "JetBrains Mono, monospace",
                                background: reg.categoria === "Regulación vigente" ? "rgba(53,98,122,0.1)" :
                                  reg.categoria === "Política pública" ? "rgba(5,150,105,0.1)" :
                                    "rgba(166,169,208,0.15)",
                                color: reg.categoria === "Regulación vigente" ? "#264a5e" :
                                  reg.categoria === "Política pública" ? "#065f46" :
                                    "#5557a0",
                              }}>{reg.categoria}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {regulaciones.datos_gov && regulaciones.datos_gov.length > 0 && (
                      <div className="sf-card">
                        <div className="sf-card-title"><span>🗂️</span> Datasets relacionados — datos.gov.co ({regulaciones.datos_gov.length})</div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                          {regulaciones.datos_gov.slice(0, 15).map((reg, i) => (
                            <div key={i} style={{
                              padding: "10px 14px", borderRadius: 8,
                              border: "1px solid #e2e5e4", background: "#fafafa",
                            }}>
                              <a href={reg.url} target="_blank" rel="noopener noreferrer"
                                style={{ fontSize: 12, fontWeight: 600, color: "#35627A", textDecoration: "none", display: "block", marginBottom: 4 }}>
                                {reg.titulo} ↗
                              </a>
                              <div style={{ fontSize: 10, color: "#8E9A98", display: "flex", gap: 8 }}>
                                <span>{reg.entidad}</span>
                                <span>·</span>
                                <span>{String(Array.isArray(reg.fecha) ? reg.fecha[0] : reg.fecha || "").substring(0, 10)}</span>
                                <span>·</span>
                                <span style={{ fontFamily: "JetBrains Mono, monospace" }}>{reg.query}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {regulaciones?.invima && regulaciones.invima.length > 0 && (
                <div className="sf-card">
                  <div className="sf-card-title"><span>🏥</span> Registros INVIMA vigentes — {sector}</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {regulaciones.invima.map((reg, i) => (
                      <div key={i} style={{
                        padding: "10px 14px", borderRadius: 8,
                        border: "1px solid #e2e5e4", background: "#fafafa",
                      }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "#1a1a1a", marginBottom: 4 }}>
                          {reg.producto}
                        </div>
                        <div style={{ fontSize: 10, color: "#8E9A98", display: "flex", gap: 8 }}>
                          <span>{reg.titular}</span>
                          <span>·</span>
                          <span>{reg.modalidad}</span>
                          <span>·</span>
                          <span style={{ fontFamily: "JetBrains Mono, monospace" }}>
                            <span>Vence: {String(reg.vencimiento || '').substring(0, 10)}</span>
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: 10, color: "#8E9A98", marginTop: 8 }}>
                    * Registros sanitarios vigentes INVIMA. Fuente: datos.gov.co
                  </div>
                </div>
              )}
            </div>
          )}

          {/* IMPACTO FINANCIERO */}
          {page === "impacto" && (
            <div>
              <div className="sf-msg">
                <div className="sf-avatar">💰</div>
                <div className="sf-bubble">
                  <p><span className="sf-accent">Impacto Financiero</span> — Estimación del impacto económico de cada escenario sobre tu empresa, basada en señales reales del entorno y el mercado de bioeconomía colombiana (~USD 530M).</p>
                </div>
              </div>
              <div className="sf-card">
                <button className="sf-btn sf-btn-primary" style={{ width: "100%" }}
                  onClick={runImpacto} disabled={loading}>
                  {loading ? "⏳ Generando..." : "💰 Generar análisis de impacto financiero"}
                </button>
              </div>

              {/* TAM / SAM / SOM / CAGR */}
              {signals.length > 0 && (
                <div className="sf-card">
                  <div className="sf-card-title"><span>📊</span> Indicadores de mercado</div>
                  {(() => {
                    const TAM = 530; // USD millones — VAB bioeconomía Colombia 2024

                    const SAM_MAP = {
                      "Agroindustria": 180,
                      "Cosméticos naturales": 45,
                      "Bioinsumos agrícolas": 75,
                      "Farmacéutica verde": 35,
                      "Alimentos funcionales": 60,
                      "Biotecnología industrial": 90,
                      "Biocomercio": 25,
                    };

                    const CAGR = 14.5; // % anual — calculado desde datos DANE 2019-2024

                    const sam = SAM_MAP[sector] || Math.round(TAM * 0.10);

                    const mejorEsc = impacto?.escenarios
                      ? [...impacto.escenarios].sort((a, b) => {
                        const usdA = Array.isArray(a.oportunidad_usd) ? a.oportunidad_usd[0] : a.oportunidad_usd || 0;
                        const usdB = Array.isArray(b.oportunidad_usd) ? b.oportunidad_usd[0] : b.oportunidad_usd || 0;
                        return usdB - usdA;
                      })[0]
                      : null;

                    const somRaw = mejorEsc
                      ? (Array.isArray(mejorEsc.oportunidad_usd) ? mejorEsc.oportunidad_usd[0] : mejorEsc.oportunidad_usd || 0) / 1e6
                      : Math.round(sam * 0.05);

                    const cuotaFactor = {
                      micro: 0.01,
                      pequena: 0.03,
                      mediana: 0.07,
                      grande: 0.15,
                    }[facturacion] || 0.05;

                    // SOM calculado directamente desde SAM y tamaño de empresa
                    // No depende del impacto financiero para evitar SOM = 0
                    const som = Math.round(sam * cuotaFactor * 10) / 10;
                    const somPct = sam > 0 ? ((som / sam) * 100).toFixed(1) : 0;

                    const kpis = [
                      {
                        label: "TAM", desc: "Mercado total disponible",
                        value: `USD ${TAM}M`, sub: "Bioeconomía Colombia 2024",
                        color: "#35627A",
                      },
                      {
                        label: "SAM", desc: "Mercado serviceable",
                        value: `USD ${sam}M`, sub: sector || "Sector no definido",
                        color: "#059669",
                      },
                      {
                        label: "SOM", desc: "Mercado obtenible",
                        value: `USD ${som.toFixed(0)}M`, sub: `${somPct}% del SAM`,
                        color: "#A6A9D0",
                      },
                      {
                        label: "CAGR", desc: "Crecimiento anual",
                        value: `${CAGR}%`, sub: "Bioeconomía 2019-2024",
                        color: "#B46258",
                      },
                    ];

                    return (
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
                        {kpis.map(({ label, desc, value, sub, color }) => (
                          <div key={label} style={{
                            padding: "12px 14px", borderRadius: 8,
                            background: `${color}08`, border: `1px solid ${color}20`,
                            textAlign: "center",
                          }}>
                            <div style={{
                              fontSize: 9, fontFamily: "JetBrains Mono, monospace",
                              color, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4,
                            }}>
                              {label}
                            </div>
                            <div style={{ fontSize: 9, color: "#8E9A98", marginBottom: 8 }}>
                              {desc}
                            </div>
                            <div style={{ fontSize: 18, fontWeight: 700, color, marginBottom: 4 }}>
                              {value}
                            </div>
                            <div style={{ fontSize: 9, color: "#8E9A98", fontFamily: "JetBrains Mono, monospace" }}>
                              {sub}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                  <div style={{ fontSize: 10, color: "#8E9A98", marginTop: 12 }}>
                    * TAM basado en VAB bioeconomía Colombia 2024 (DANE). SAM estimado por sector. SOM calculado desde el escenario de mayor oportunidad. CAGR calculado 2019-2024.
                  </div>
                </div>
              )}

              {impacto && impacto.escenarios && (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {impacto.escenarios.map((esc, i) => {
                    const isOpp = !esc.riesgo || esc.oportunidad_usd > 0;
                    const color = isOpp ? "#35627A" : "#B46258";
                    const bg = isOpp ? "rgba(53,98,122,0.04)" : "rgba(180,98,88,0.04)";
                    const usd = esc.oportunidad_usd || 0;
                    const brlVal = usd >= 100000000 ? 8
                      : usd >= 50000000 ? 7
                        : usd >= 20000000 ? 6
                          : usd >= 10000000 ? 5
                            : usd >= 5000000 ? 4
                              : usd >= 1000000 ? 3 : 2;
                    const brlLabel = brlVal >= 7 ? "Alta viabilidad comercial"
                      : brlVal >= 5 ? "Viabilidad en desarrollo"
                        : "Etapa exploratoria";
                    return (
                      <div key={i} className="sf-card" style={{ borderLeft: `4px solid ${color}`, background: bg }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                          <div className="sf-card-title" style={{ color, margin: 0 }}>
                            {esc.cluster}
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{
                              padding: "3px 8px", borderRadius: 6,
                              fontSize: 9, fontWeight: 700,
                              fontFamily: "JetBrains Mono, monospace",
                              background: "rgba(5,150,105,0.1)", color: "#065f46",
                              border: "1px solid rgba(5,150,105,0.2)",
                            }}>
                              BRL {brlVal}/9
                            </span>
                            {esc.oportunidad_usd > 0 && (
                              <span style={{
                                padding: "4px 12px", borderRadius: 20,
                                fontSize: 13, fontWeight: 700,
                                background: "rgba(5,150,105,0.1)", color: "#065f46",
                                border: "1px solid rgba(5,150,105,0.2)",
                              }}>
                                USD {esc.oportunidad_usd >= 1000000
                                  ? `~${(esc.oportunidad_usd / 1000000).toFixed(0)}M`
                                  : `~${(esc.oportunidad_usd / 1000).toFixed(0)}K`}
                              </span>
                            )}
                          </div>
                        </div>
                        <div style={{
                          fontSize: 10, color: "#8E9A98",
                          fontFamily: "JetBrains Mono, monospace",
                          marginBottom: 12, padding: "4px 8px",
                          background: "rgba(5,150,105,0.05)", borderRadius: 4,
                        }}>
                          BRL {brlVal}/9 · {brlLabel}
                        </div>
                        <p style={{ fontSize: 13, color: "#1a1a1a", lineHeight: 1.7, marginBottom: 12 }}>
                          {esc.narrativa}
                        </p>
                        {esc.riesgo && (
                          <div style={{
                            padding: "8px 12px", borderRadius: 6,
                            background: "rgba(180,98,88,0.06)",
                            border: "1px solid rgba(180,98,88,0.15)",
                            fontSize: 12, color: "#8a3f35",
                          }}>
                            <strong>⚠️ Riesgo:</strong> {esc.riesgo}
                          </div>
                        )}

                        {montecarlo && (() => {
                          const clusterStr = Array.isArray(esc.cluster) ? esc.cluster[0] : esc.cluster;
                          const mc = montecarlo.find(m => m.cluster === clusterStr);
                          if (!mc) return null;
                          return (
                            <div style={{
                              marginTop: 10, padding: "8px 12px", borderRadius: 6,
                              background: "rgba(53,98,122,0.04)",
                              border: "1px solid rgba(53,98,122,0.12)",
                              fontSize: 11, color: "#264a5e",
                              display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center",
                            }}>
                              <span style={{ fontWeight: 700 }}>🎲 Monte Carlo</span>
                              <span style={{ fontFamily: "JetBrains Mono, monospace" }}>
                                IC 80%: USD {(mc.p10 / 1e6).toFixed(0)}M – USD {(mc.p90 / 1e6).toFixed(0)}M
                              </span>
                              <span style={{ fontFamily: "JetBrains Mono, monospace" }}>
                                · Mediana: USD {(mc.p50 / 1e6).toFixed(0)}M
                              </span>
                            </div>
                          );
                        })()}

                        {/* Índice de productividad */}
                        {(() => {
                          const prod = calcProductividad(sector, esc);
                          const base = PRODUCTIVIDAD_SECTORIAL[sector]
                            ? Object.values(PRODUCTIVIDAD_SECTORIAL[sector]).reduce((a, b) => a + b, 0) / 4
                            : 0.65;
                          const delta = prod - base;
                          const pct = Math.abs(Math.round(delta * 100));
                          const direccion = delta > 0.005 ? "incrementaría" : delta < -0.005 ? "disminuiría" : "mantendría estable";
                          const color = delta > 0.01 ? "#059669" : delta < -0.01 ? "#B46258" : "#35627A";

                          return (
                            <div style={{
                              marginTop: 10, padding: "8px 12px", borderRadius: 6,
                              background: `${color}08`,
                              border: `1px solid ${color}25`,
                              fontSize: 11, color,
                              display: "flex", alignItems: "center", gap: 8,
                            }}>
                              <span style={{ fontWeight: 700 }}>⚙️ Productividad</span>
                              <span>
                                Este escenario <strong>{direccion}</strong> tu productividad
                                {pct > 0 ? ` un ${pct}%` : ""} respecto al promedio sectorial
                                {" "}(Werther & Davis)
                              </span>
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                  <div style={{ fontSize: 10, color: "#8E9A98", textAlign: "center", padding: "8px 0" }}>
                    * Estimaciones basadas en VAB bioeconomía Colombia 2024 (DANE). No constituyen proyecciones financieras formales.
                  </div>
                </div>
              )}
            </div>
          )}

          {page === "sostenibilidad" && (
            <div>
              <div className="sf-msg">
                <div className="sf-avatar">♻️</div>
                <div className="sf-bubble">
                  <p><span className="sf-accent">Inteligencia Sostenible.</span> Impacto ambiental de tu estrategia en {departamento || "el territorio"}.</p>
                </div>
              </div>

              {!sostenibilidad && (
                <div className="sf-card" style={{ textAlign: "center", color: "#8E9A98", fontSize: 13 }}>
                  Ejecuta un análisis con departamento seleccionado para ver los indicadores de sostenibilidad.
                </div>
              )}

              {sostenibilidad && (
                <div>
                  {/* Indicadores ambientales */}
                  <div className="sf-card">
                    <div className="sf-card-title"><span>🌿</span> Indicadores ambientales — {sostenibilidad.departamento}</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 8 }}>
                      {[
                        { label: "Cobertura boscosa", value: `${sostenibilidad.indicadores?.cobertura_boscosa}%`, icon: "🌳" },
                        { label: "Índice hídrico", value: `${sostenibilidad.indicadores?.indice_hidrico}/10`, icon: "💧" },
                        { label: "Biodiversidad", value: sostenibilidad.indicadores?.biodiversidad, icon: "🦋" },
                        { label: "Riesgo deforestación", value: sostenibilidad.indicadores?.riesgo_deforestacion, icon: "⚠️" },
                        { label: "Áreas protegidas", value: `${sostenibilidad.indicadores?.areas_protegidas}%`, icon: "🏞️" },
                        { label: "Registros SiB", value: sostenibilidad.indicadores?.n_registros_bio ? sostenibilidad.indicadores.n_registros_bio.toLocaleString() : "N/D", icon: "🔬" },
                        { label: "Temperatura", value: sostenibilidad.indicadores?.temperatura ? `${sostenibilidad.indicadores.temperatura}°C` : "N/D", icon: "🌡️" },
                        { label: "Precipitación", value: sostenibilidad.indicadores?.precipitacion ? `${sostenibilidad.indicadores.precipitacion} mm/día` : "N/D", icon: "🌧️" },
                      ].map((kpi, i) => (
                        <div key={i} style={{
                          padding: "10px 14px", borderRadius: 8,
                          border: "1px solid #e2e5e4", background: "#fafafa",
                        }}>
                          <div style={{ fontSize: 10, color: "#8E9A98", marginBottom: 2 }}>{kpi.icon} {kpi.label}</div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: "#1a1a1a" }}>{kpi.value}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Impacto por escenario */}
                  {sostenibilidad.escenarios_impacto?.length > 0 && (
                    <div className="sf-card">
                      <div className="sf-card-title"><span>📊</span> Impacto ambiental por escenario</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
                        {sostenibilidad.escenarios_impacto.map((esc, i) => {
                          const colorBalance = esc.balance === "Sostenible" ? "#059669" : esc.balance === "Moderado" ? "#D97706" : "#B46258";
                          return (
                            <div key={i} style={{
                              padding: "10px 14px", borderRadius: 8,
                              border: `1px solid ${colorBalance}30`,
                              background: `${colorBalance}08`,
                            }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                                <span style={{ fontWeight: 700, fontSize: 12, color: "#1a1a1a" }}>{esc.cluster}</span>
                                <span style={{
                                  padding: "2px 8px", borderRadius: 20, fontSize: 10, fontWeight: 600,
                                  background: `${colorBalance}15`, color: colorBalance,
                                }}>{esc.balance}</span>
                              </div>
                              <div style={{ display: "flex", gap: 16, fontSize: 11, color: "#8E9A98" }}>
                                <span>💧 Huella hídrica: <strong>{esc.huella_hidrica}/10</strong></span>
                                <span>🌫️ CO2: <strong>{esc.co2_index}/10</strong></span>
                                <span>🦋 Biodiversidad: <strong>{esc.riesgo_bio}</strong></span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Análisis LLM */}
                  {sostenibilidad.analisis && (
                    <div className="sf-card">
                      <div className="sf-card-title"><span>🧠</span> Análisis de impacto ambiental</div>
                      <div className="sf-insight"><ReactMarkdown>{sostenibilidad.analisis}</ReactMarkdown></div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* MICMAC */}
          {page === "micmac" && (
            <div>
              <div className="sf-msg">
                <div className="sf-avatar">🔗</div>
                <div className="sf-bubble">
                  <p><span className="sf-accent">Matriz de Impacto Cruzado</span> — Identificación de variables clave del sistema bioeconómico.</p>
                </div>
              </div>
              <div className="sf-card">
                <button className="sf-btn sf-btn-primary" style={{ width: "100%" }}
                  onClick={runMicmac} disabled={loading}>
                  {loading ? "⏳ Generando..." : "🔗 Generar análisis MICMAC"}
                </button>
              </div>
              {micmac && (
                <div>
                  {/* Narrativa */}
                  {micmac.narrativa && (
                    <div className="sf-card">
                      <div className="sf-card-title"><span>📝</span> Análisis estratégico</div>
                      <div className="sf-insight" style={{ fontSize: 13, color: "#1a1a1a", lineHeight: 1.7 }}>
                        <ReactMarkdown>{micmac.narrativa}</ReactMarkdown>
                      </div>
                    </div>
                  )}

                  {/* Gráfico cartesiano MICMAC */}
                  <div className="sf-card">
                    <div className="sf-card-title"><span>📐</span> Plano motricidad-dependencia</div>
                    <MicmacPlot variables={micmac.variables || []} />
                    <div style={{ display: "flex", gap: 16, marginTop: 12, flexWrap: "wrap" }}>
                      {[
                        { label: "Poder", color: "#35627A" },
                        { label: "Conflicto", color: "#B46258" },
                        { label: "Autónoma", color: "#8E9A98" },
                        { label: "Resultado", color: "#059669" },
                      ].map(({ label, color }) => (
                        <div key={label} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 10, color: "#8E9A98" }}>
                          <div style={{ width: 10, height: 10, borderRadius: "50%", background: color }} />
                          {label}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {page === "plan" && (
            <div>
              <div className="sf-msg">
                <div className="sf-avatar">🧭</div>
                <div className="sf-bubble">
                  <p><span className="sf-accent">Plan Estratégico.</span> Resumen ejecutivo orientado a tu pregunta, con viabilidad integrada de competitividad y sostenibilidad.</p>
                </div>
              </div>
              <div className="sf-card">
                <button
                  className="sf-btn sf-btn-primary"
                  style={{ width: "100%" }}
                  onClick={async () => {
                    if (!signals.length) return;
                    setLoading(true);
                    setError(null);
                    setStatusMsg("generando plan...");
                    addLog("Generando plan estratégico...");
                    try {
                      const scens = buildScenarios(signals);
                      const scensSummary = scens.slice(0, 4).map(s =>
                        `- ${s.cluster} (${s.scenario_type}) | ROII: ${s.roii} | RMI: ${s.rmi} | Señales: ${s.signals.length}`
                      ).join('\n');
                      const sostenibilidadCtx = sostenibilidad?.escenarios_impacto?.length > 0
                        ? sostenibilidad.escenarios_impacto.map(e =>
                          `- ${e.cluster}: Huella hidrica ${e.huella_hidrica}/10 | CO2 ${e.co2_index}/10 | Balance: ${e.balance}`
                        ).join('\n')
                        : "";
                      const indicadoresCtx = sostenibilidad?.indicadores
                        ? `Departamento: ${sostenibilidad.departamento} | Biodiversidad: ${sostenibilidad.indicadores.biodiversidad} | Cobertura boscosa: ${sostenibilidad.indicadores.cobertura_boscosa}%`
                        : "";
                      const p = await generatePlan(problem, signals.length, scens.length, scensSummary, {
                        sector,
                        facturacion,
                        plazo: plazoDecision,
                        obstaculo: obstaculoPrincipal,
                        sostenibilidad_escenarios: sostenibilidadCtx,
                        indicadores_ambientales: indicadoresCtx,
                      }, signals);
                      if (p && (p.executive || p.recommendations)) {
                        setPlan({
                          executive: Array.isArray(p.executive) ? p.executive[0] : p.executive,
                          recommendations: Array.isArray(p.recommendations) ? p.recommendations[0] : p.recommendations,
                          canvas: p.canvas || null,
                        });
                        addLog("Plan generado");
                      } else {
                        addLog("El modelo no generó el plan. Intenta de nuevo.", "warn");
                        setError("No se pudo generar el plan.");
                      }
                    } catch (e) {
                      console.error("Plan error:", e);
                      addLog("Error al generar el plan estratégico", "error");
                      setError("Error al conectar con el servidor.");
                    }
                    setStatusMsg("agente listo");
                    setLoading(false);
                  }}
                  disabled={loading}
                >
                  {loading ? "⏳ Generando..." : "🧠 Generar plan estratégico con IA"}
                </button>
              </div>
              {plan && (
                <div>
                  <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
                    <ExportarPDF
                      datos={{
                        problem,
                        sector,
                        departamento,
                        facturacion,
                        plazo: plazoDecision,
                        objetivo,
                        foda,
                        horizontes,
                        impacto,
                        sostenibilidad,
                        micmac,
                        plan,
                        signals,
                        montecarlo,
                        tam: (() => {
                          const SAM_MAP = { "Agroindustria": 180, "Cosméticos naturales": 45, "Bioinsumos agrícolas": 75, "Farmacéutica verde": 35, "Alimentos funcionales": 60, "Biotecnología industrial": 90, "Biocomercio": 25 };
                          return SAM_MAP[sector] || 53;
                        })(),
                        sam: (() => {
                          const SAM_MAP = { "Agroindustria": 180, "Cosméticos naturales": 45, "Bioinsumos agrícolas": 75, "Farmacéutica verde": 35, "Alimentos funcionales": 60, "Biotecnología industrial": 90, "Biocomercio": 25 };
                          return SAM_MAP[sector] || 53;
                        })(),
                        som: (() => {
                          const SAM_MAP = { "Agroindustria": 180, "Cosméticos naturales": 45, "Bioinsumos agrícolas": 75, "Farmacéutica verde": 35, "Alimentos funcionales": 60, "Biotecnología industrial": 90, "Biocomercio": 25 };
                          const sam = SAM_MAP[sector] || 53;
                          const cuota = { micro: 0.01, pequena: 0.03, mediana: 0.07, grande: 0.15 }[facturacion] || 0.05;
                          return Math.round(sam * cuota * 10) / 10;
                        })(),
                      }}
                      disabled={!plan}
                    />
                  </div>
                  <div className="sf-card">
                    <div className="sf-card-title"><span>📝</span> Resumen ejecutivo</div>
                    <div className="sf-insight"><ReactMarkdown>{plan.executive || "No disponible."}</ReactMarkdown></div>
                  </div>
                  {plan.recommendations && plan.recommendations.trim() !== "" && (
                    <div className="sf-card">
                      <div className="sf-card-title"><span>💡</span> Recomendaciones por escenario</div>
                      <div className="sf-insight"><ReactMarkdown>{plan.recommendations}</ReactMarkdown></div>
                    </div>
                  )}
                  {/* Modelos de negocio por escenario */}
                  {plan.canvas?.canvas?.length > 0 && (
                    <div className="sf-card">
                      <div className="sf-card-title"><span>🗂️</span> Modelos de negocio por escenario</div>
                      {plan.canvas.canvas.map((esc, i) => (
                        <div key={i} style={{
                          marginBottom: 16, borderRadius: 8,
                          border: "1px solid #e2e5e4", overflow: "hidden"
                        }}>
                          <div style={{
                            background: "#1a2744", color: "#fff",
                            padding: "8px 14px", fontWeight: 700, fontSize: 12
                          }}>
                            {esc.escenario}
                          </div>
                          <div style={{
                            display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
                            gap: 1, background: "#e2e5e4"
                          }}>
                            {[
                              { label: "Propuesta de valor", value: esc.propuesta_valor, color: "#059669" },
                              { label: "Segmento cliente", value: esc.segmento_cliente, color: "#35627A" },
                              { label: "Canales", value: esc.canales, color: "#35627A" },
                              { label: "Fuentes de ingreso", value: esc.fuentes_ingreso, color: "#059669" },
                              { label: "Recursos clave", value: esc.recursos_clave, color: "#8E9A98" },
                              { label: "Actividades clave", value: esc.actividades_clave, color: "#8E9A98" },
                              { label: "Socios clave", value: esc.socios_clave, color: "#8E9A98" },
                              { label: "Estructura de costos", value: esc.estructura_costos, color: "#B46258" },
                              {
                                label: "Impacto ambiental", value: esc.impacto_ambiental,
                                color: esc.impacto_ambiental === "Sostenible" ? "#059669" :
                                  esc.impacto_ambiental === "Moderado" ? "#D97706" : "#B46258"
                              },
                            ].map((cell, j) => (
                              <div key={j} style={{ background: "#fff", padding: "8px 10px" }}>
                                <div style={{ fontSize: 9, color: cell.color, fontWeight: 700, marginBottom: 3 }}>
                                  {cell.label.toUpperCase()}
                                </div>
                                <div style={{ fontSize: 11, color: "#1a1a1a", lineHeight: 1.4 }}>
                                  {cell.value || "—"}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* MIS ANÁLISIS */}
          {page === "mis-analisis" && (
            <div>
              <div className="sf-msg">
                <div className="sf-avatar">📂</div>
                <div className="sf-bubble">
                  <p><span className="sf-accent">Mis análisis</span> — Historial de análisis guardados en tu cuenta.</p>
                </div>
              </div>

              <div className="sf-card">
                <button className="sf-btn sf-btn-primary" style={{ width: "100%" }} onClick={cargarMisAnalisis} disabled={loading}>
                  🔄 Actualizar historial
                </button>
              </div>

              {analisisSeleccionado ? (
                <div>
                  <div className="sf-card" style={{ borderLeft: "4px solid #35627A" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                      <div className="sf-card-title" style={{ margin: 0 }}>📋 Análisis seleccionado</div>
                      <button
                        type="button"
                        onClick={() => setAnalisisSeleccionado(null)}
                        style={{ background: "rgba(0,0,0,0.06)", border: "none", borderRadius: "50%", width: 28, height: 28, cursor: "pointer", fontSize: 16, color: "#333" }}
                      >
                        ×
                      </button>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a1a", marginBottom: 8 }}>
                      {analisisSeleccionado.problema}
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ padding: "2px 8px", borderRadius: 20, fontSize: 9, fontWeight: 600, background: "rgba(53,98,122,0.1)", color: "#35627A", fontFamily: "JetBrains Mono, monospace" }}>
                        {analisisSeleccionado.sector}
                      </span>
                      <span style={{ padding: "2px 8px", borderRadius: 20, fontSize: 9, fontWeight: 600, background: "rgba(142,154,152,0.1)", color: "#8E9A98", fontFamily: "JetBrains Mono, monospace" }}>
                        {analisisSeleccionado.signals_count} señales
                      </span>
                      <span style={{ padding: "2px 8px", borderRadius: 20, fontSize: 9, fontWeight: 600, background: "rgba(142,154,152,0.1)", color: "#8E9A98", fontFamily: "JetBrains Mono, monospace" }}>
                        {new Date(analisisSeleccionado.created_at).toLocaleDateString("es-CO", { day: "2-digit", month: "short", year: "numeric" })}
                      </span>
                    </div>
                  </div>

                  {analisisSeleccionado.foda && (() => {
                    const f = typeof analisisSeleccionado.foda === "string"
                      ? JSON.parse(analisisSeleccionado.foda)
                      : analisisSeleccionado.foda;

                    return (
                      <div className="sf-card">
                        <div className="sf-card-title"><span>🔲</span> Análisis FODA</div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                          {[
                            { key: "fortalezas", label: "💪 Fortalezas", color: "#35627A", bg: "rgba(53,98,122,0.06)" },
                            { key: "oportunidades", label: "🚀 Oportunidades", color: "#059669", bg: "rgba(5,150,105,0.06)" },
                            { key: "debilidades", label: "⚠️ Debilidades", color: "#B46258", bg: "rgba(180,98,88,0.06)" },
                            { key: "amenazas", label: "🛑 Amenazas", color: "#8a3f35", bg: "rgba(138,63,53,0.06)" },
                          ].map(({ key, label, color, bg }) => (
                            <div key={key} style={{ background: bg, borderLeft: `3px solid ${color}`, borderRadius: 8, padding: "12px 14px" }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color, marginBottom: 8 }}>{label}</div>
                              <ul style={{ margin: 0, paddingLeft: 16 }}>
                                {(f[key] || []).map((item, i) => (
                                  <li key={i} style={{ fontSize: 12, color: "#1a1a1a", marginBottom: 6, lineHeight: 1.5 }}>{item}</li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {analisisSeleccionado.horizontes && (() => {
                    const h = typeof analisisSeleccionado.horizontes === "string"
                      ? JSON.parse(analisisSeleccionado.horizontes)
                      : analisisSeleccionado.horizontes;

                    return (
                      <div className="sf-card">
                        <div className="sf-card-title"><span>⏳</span> Horizontes McKinsey</div>
                        {[
                          { key: "h1", label: "H1 · Corto plazo", color: "#B46258" },
                          { key: "h2", label: "H2 · Mediano plazo", color: "#35627A" },
                          { key: "h3", label: "H3 · Largo plazo", color: "#059669" },
                        ].map(({ key, label, color }) => {
                          const a = h.analisis?.[key];
                          if (!a) return null;
                          return (
                            <div key={key} style={{ borderLeft: `3px solid ${color}`, padding: "12px 14px", marginBottom: 12, background: `${color}06`, borderRadius: 8 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color, marginBottom: 6 }}>{label}</div>
                              <p style={{ fontSize: 12, color: "#1a1a1a", lineHeight: 1.6, margin: 0 }}>{a.descripcion}</p>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}

                  {analisisSeleccionado.impacto && (() => {
                    const imp = typeof analisisSeleccionado.impacto === "string"
                      ? JSON.parse(analisisSeleccionado.impacto)
                      : analisisSeleccionado.impacto;

                    return (
                      <div className="sf-card">
                        <div className="sf-card-title"><span>💰</span> Impacto Financiero</div>
                        {(imp.escenarios || []).map((esc, i) => {
                          const clusterStr = Array.isArray(esc.cluster) ? esc.cluster[0] : esc.cluster;
                          const narrativa = Array.isArray(esc.narrativa) ? esc.narrativa[0] : esc.narrativa;
                          const usd = Array.isArray(esc.oportunidad_usd) ? esc.oportunidad_usd[0] : esc.oportunidad_usd || 0;

                          return (
                            <div key={i} style={{ borderLeft: "3px solid #35627A", padding: "12px 14px", marginBottom: 12, background: "rgba(53,98,122,0.04)", borderRadius: 8 }}>
                              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                                <div style={{ fontSize: 12, fontWeight: 700, color: "#35627A" }}>{clusterStr}</div>
                                {usd > 0 && (
                                  <span style={{ fontSize: 12, fontWeight: 700, color: "#065f46" }}>
                                    USD {usd >= 1000000 ? `~${(usd / 1000000).toFixed(0)}M` : `~${(usd / 1000).toFixed(0)}K`}
                                  </span>
                                )}
                              </div>
                              <p style={{ fontSize: 12, color: "#1a1a1a", lineHeight: 1.6, margin: 0 }}>{narrativa}</p>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}

                  {analisisSeleccionado.plan && (
                    <div className="sf-card">
                      <div className="sf-card-title"><span>🧭</span> Plan Estratégico</div>
                      <div className="sf-insight">
                        <ReactMarkdown>{analisisSeleccionado.plan}</ReactMarkdown>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  {misAnalisis.length === 0 ? (
                    <div className="sf-card" style={{ textAlign: "center", color: "#8E9A98", fontSize: 13 }}>
                      No hay análisis guardados aún.
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      {misAnalisis.map((a) => (
                        <div key={a.id} className="sf-card" style={{ borderLeft: "4px solid #35627A" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a1a", flex: 1, lineHeight: 1.4 }}>
                              {a.problema}
                            </div>
                            <div style={{ fontSize: 9, color: "#8E9A98", fontFamily: "JetBrains Mono, monospace", marginLeft: 12, flexShrink: 0 }}>
                              {new Date(a.created_at).toLocaleDateString("es-CO", { day: "2-digit", month: "short", year: "numeric" })}
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                            <span style={{ padding: "2px 8px", borderRadius: 20, fontSize: 9, fontWeight: 600, background: "rgba(53,98,122,0.1)", color: "#35627A", fontFamily: "JetBrains Mono, monospace" }}>
                              {a.sector || "Sin sector"}
                            </span>
                            <span style={{ padding: "2px 8px", borderRadius: 20, fontSize: 9, fontWeight: 600, background: "rgba(142,154,152,0.1)", color: "#8E9A98", fontFamily: "JetBrains Mono, monospace" }}>
                              {a.signals_count} señales
                            </span>
                            <div style={{ display: "flex", gap: 6, marginLeft: "auto" }}>
                              <button
                                type="button"
                                onClick={async () => {
                                  const { data } = await supabase
                                    .from("projects")
                                    .select("*")
                                    .eq("id", a.id)
                                    .single();

                                  if (data) {
                                    setAnalisisSeleccionado(data);
                                  }
                                }}
                                style={{
                                  padding: "4px 10px",
                                  borderRadius: 6,
                                  fontSize: 11,
                                  fontWeight: 600,
                                  cursor: "pointer",
                                  border: "1px solid rgba(53,98,122,0.3)",
                                  background: "rgba(53,98,122,0.08)",
                                  color: "#35627A",
                                }}
                              >
                                📋 Ver
                              </button>
                              <button
                                type="button"
                                onClick={() => restaurarAnalisis(a)}
                                style={{
                                  padding: "4px 10px",
                                  borderRadius: 6,
                                  fontSize: 11,
                                  fontWeight: 600,
                                  cursor: "pointer",
                                  border: "1px solid rgba(5,150,105,0.3)",
                                  background: "rgba(5,150,105,0.08)",
                                  color: "#059669",
                                }}
                              >
                                🔄 Restaurar
                              </button>
                              <button
                                type="button"
                                onClick={() => eliminarAnalisis(a.id)}
                                style={{
                                  padding: "4px 10px",
                                  borderRadius: 6,
                                  fontSize: 11,
                                  fontWeight: 600,
                                  cursor: "pointer",
                                  border: "1px solid rgba(180,98,88,0.3)",
                                  background: "rgba(180,98,88,0.08)",
                                  color: "#B46258",
                                }}
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
